import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ItemPhotosModal from './ItemPhotosModal';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AddItemModal from './AddItemModal';
import AdvancedFFEFeatures from './AdvancedFFEFeatures';
import { getRoomColor, getCategoryColor, getColorByIndex } from '../utils/roomColors';
import { getStatusColor, STATUS_COLORS } from '../utils/statusColors';
import { toast } from 'sonner';
import { FolderInput, Lock, Unlock } from 'lucide-react';

// Dedicated PAINT controls shown inline on paint / wallpaper rows. Keyed off the
// SUBCATEGORY (where Paint / Wallpaper live) so tile / floor / slab rows that
// share a "Paint, Wallpaper & Finishes" parent don't get a sheen picker.
const PAINT_SHEENS = ['Flat', 'Matte', 'Eggshell', 'Satin', 'Semi-Gloss', 'Gloss', 'High-Gloss'];
const PAINT_AREAS = ['Walls', 'Trim', 'Ceiling', 'Doors', 'Cabinets', 'Accent'];
const PAINT_SPEC_CHECKS = ['Color', 'Finish'];
const CABINET_SPEC_CHECKS = ['Color', 'Finish', 'Wood Species'];
const isPaintRow = (categoryName, subName) => {
  const sub = (subName || '').toLowerCase();
  // Sheen + Color/Finish checks for Paint, Wallpaper/wallcovering and Finishes rows.
  return /paint|wallpaper|wall ?covering|wallcovering|grasscloth|mural|finish/.test(sub);
};
const isCabinetRow = (categoryName, subName) => {
  const sub = (subName || '').toLowerCase();
  // Cabinets / Vanity rows get Sheen + Color/Finish/Wood-Species checks.
  return /cabinet|vanity/.test(sub);
};
// The paint-area chips only make sense for paint & wallcovering; cabinet/finish rows get Sheen only.
const showsPaintAreas = (subName) => {
  const sub = (subName || '').toLowerCase();
  return /paint|wallpaper|wall ?covering|wallcovering|grasscloth|mural/.test(sub);
};
// "Proper" paint (not wallpaper/finish) gets a SHEEN PER PLACEMENT — the same colour
// is often Flat on the walls but Semi-Gloss on the trim.
const isPaintProper = (subName) => {
  const sub = (subName || '').toLowerCase();
  return /paint/.test(sub) && !/wallpaper|wall ?covering|wallcovering|grasscloth|mural/.test(sub);
};

const ExactFFESpreadsheet = ({ 
  project, 
  roomColors, 
  categoryColors, 
  itemStatuses = [],
  vendorTypes = [],
  carrierTypes = [],
  onDeleteRoom, 
  onBulkDeleteRooms,
  onAddRoom,
  onReload 
}) => {
  // ✅ DEBUG LOGGING TO FIND EMPTY SPREADSHEET ISSUE
  console.log('📊 ExactFFESpreadsheet - Project data:', project);
  console.log('📊 ExactFFESpreadsheet - Rooms count:', project?.rooms?.length || 0);
  console.log('📊 ExactFFESpreadsheet - First room:', project?.rooms?.[0] || 'No rooms');
  
  const [showAddItem, setShowAddItem] = useState(false);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedRoomsForDelete, setSelectedRoomsForDelete] = useState(new Set());
  
  // Load expanded states from localStorage
  const [expandedRooms, setExpandedRooms] = useState(() => {
    const saved = localStorage.getItem('ffe_expandedRooms');
    return saved ? JSON.parse(saved) : {};
  });
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const saved = localStorage.getItem('ffe_expandedCategories');
    return saved ? JSON.parse(saved) : {};
  });
  
  const [expandedImage, setExpandedImage] = useState(null);  // For image modal
  const [photoItem, setPhotoItem] = useState(null);          // line item whose photos are open
  const [refreshingPhotos, setRefreshingPhotos] = useState(false); // bulk hi-res photo refresh
  const [photoProgress, setPhotoProgress] = useState(null);  // {done,total,updated}
  const [refreshingItemId, setRefreshingItemId] = useState(null); // per-item hi-res refresh
  const [describingItemId, setDescribingItemId] = useState(null); // per-item AI description
  const [bulkStatus, setBulkStatus] = useState('');               // mass status change target
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set()); // hand-picked rows for bulk status
  
  // Room photos state - for displaying walkthrough photos
  const [roomPhotos, setRoomPhotos] = useState({});
  const [expandedPhotoRooms, setExpandedPhotoRooms] = useState({});
  const [selectedPhotoView, setSelectedPhotoView] = useState(null);

  // "Grab & move" an item to another room/category (reliable alternative to
  // dragging across this very wide, collapsible spreadsheet).
  const [moveItem, setMoveItem] = useState(null);   // the item being relocated
  const [moveRoomId, setMoveRoomId] = useState('');
  const [moveCategoryId, setMoveCategoryId] = useState('');
  const [moveSubId, setMoveSubId] = useState('');
  const [moveBusy, setMoveBusy] = useState(false);

  const openMoveModal = (item) => {
    const rooms = project?.rooms || [];
    const firstRoom = rooms[0];
    const firstCat = firstRoom?.categories?.[0];
    const firstSub = firstCat?.subcategories?.[0];
    setMoveItem(item);
    setMoveRoomId(firstRoom?.id || '');
    setMoveCategoryId(firstCat?.id || '');
    setMoveSubId(firstSub?.id || '');
  };

  const onMoveRoomChange = (roomId) => {
    const room = (project?.rooms || []).find((r) => r.id === roomId);
    const firstCat = room?.categories?.[0];
    const firstSub = firstCat?.subcategories?.[0];
    setMoveRoomId(roomId);
    setMoveCategoryId(firstCat?.id || '');
    setMoveSubId(firstSub?.id || '');
  };

  const onMoveCategoryChange = (catId) => {
    const room = (project?.rooms || []).find((r) => r.id === moveRoomId);
    const cat = (room?.categories || []).find((c) => c.id === catId);
    const firstSub = cat?.subcategories?.[0];
    setMoveCategoryId(catId);
    setMoveSubId(firstSub?.id || '');
  };

  const performItemMove = async () => {
    if (!moveItem || !moveSubId) { toast.error('Pick a destination room, category and section first.'); return; }
    const backendUrl = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
    const room = (project?.rooms || []).find((r) => r.id === moveRoomId);
    const cat = (room?.categories || []).find((c) => c.id === moveCategoryId);
    const dest = `${room?.name || 'room'} › ${cat?.name || 'category'}`;
    setMoveBusy(true);
    try {
      if (moveItem.bulk) {
        const ids = Array.from(selectedItemIds);
        if (!ids.length) { toast.error('No items selected.'); setMoveBusy(false); return; }
        const resp = await fetch(`${backendUrl}/api/items/bulk-move`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_ids: ids, subcategory_id: moveSubId }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const d = await resp.json();
        toast.success(`Moved ${d.moved} item(s) to ${dest}`);
        clearSelection();
      } else {
        if (moveItem.subcategoryId && moveItem.subcategoryId === moveSubId) {
          toast('That item is already in that section.');
          setMoveBusy(false);
          return;
        }
        const resp = await fetch(`${backendUrl}/api/items/${moveItem.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subcategory_id: moveSubId }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        toast.success(`Moved "${(moveItem.name || 'item').slice(0, 40)}" to ${dest}`);
      }
      setMoveItem(null);
      if (onReload) onReload();
    } catch (e) {
      toast.error('Could not move: ' + (e.message || e));
    } finally {
      setMoveBusy(false);
    }
  };

  // Open the image lightbox as a GALLERY (main photo + extra photos + finish
  // swatch) so the user can page through an item's pictures with ← / → arrows.
  const openLightbox = (item, url) => {
    const imgs = [...new Set([item?.image_url, ...(item?.photos || []), item?.finish_image].filter(Boolean))];
    const list = imgs.length ? imgs : [url].filter(Boolean);
    const idx = Math.max(0, list.indexOf(url));
    setExpandedImage({ images: list, index: idx });
  };
  const stepLightbox = (dir) => {
    setExpandedImage((cur) => {
      if (!cur || !cur.images || cur.images.length < 2) return cur;
      const n = cur.images.length;
      return { ...cur, index: (cur.index + dir + n) % n };
    });
  };

  // Close any open image lightbox on Escape, and page an image gallery with the
  // arrow keys, so it never feels "stuck".
  useEffect(() => {
    if (!expandedImage && !selectedPhotoView) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setExpandedImage(null);
        setSelectedPhotoView(null);
      } else if (expandedImage) {
        if (e.key === 'ArrowRight') stepLightbox(1);
        else if (e.key === 'ArrowLeft') stepLightbox(-1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedImage, selectedPhotoView]);

  // FILTER STATE - MAKE IT ACTUALLY WORK
  const [filteredProject, setFilteredProject] = useState(project);
  
  // ✅ SCRAPER CLIPBOARD STATE - For pasting scraped data into specific rows
  const [scraperClipboard, setScraperClipboard] = useState(null);
  const [selectedItemForPaste, setSelectedItemForPaste] = useState(null); // Which item row is selected
  const [showScraperNotification, setShowScraperNotification] = useState(false);
  
  // 🔗 PUNCH LIST LINKED ITEMS - For highlighting items on punch list
  // Map format: { itemId: { completed: boolean, status: string, punch_title: string } }
  const [linkedPunchItems, setLinkedPunchItems] = useState({});
  // 🔗 TO-DO LINKED ITEMS - For highlighting items on to-do list
  // Map format: { itemId: { completed: boolean, status: string } }
  const [todoLinkedItems, setTodoLinkedItems] = useState(new Map());

  // FLOORS - rooms are grouped under the floor they sit on. Same model as the
  // Checklist sheet so a room reads the same way on every tab.
  const [expandedFloors, setExpandedFloors] = useState({});
  const getFloorOrder = () => {
    const p = filteredProject || project;
    if (p?.floor_order?.length) return p.floor_order;
    const floors = [];
    (p?.rooms || []).forEach(r => { const f = r.floor || '1ST FLOOR'; if (!floors.includes(f)) floors.push(f); });
    return floors.length ? floors : ['1ST FLOOR'];
  };
  const toggleFloor = (f) => setExpandedFloors(prev => ({ ...prev, [f]: prev[f] === false ? true : false }));
  const saveFloorOrder = async (newOrder) => {
    try { const bu = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);
      await fetch(`${bu}/api/projects/${project.id}`, { method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify({floor_order: newOrder}) });
    } catch(e) { console.error(e); }
  };
  const moveFloor = async (from, to) => { const f = [...getFloorOrder()]; const [m] = f.splice(from,1); f.splice(to,0,m); await saveFloorOrder(f); if (onReload) await onReload(); };
  const addFloor = async (name) => { const f = getFloorOrder(); if (!f.includes(name)) { await saveFloorOrder([...f, name]); if (onReload) await onReload(); } };
  const deleteFloor = async (fn) => { const f = getFloorOrder().filter(x=>x!==fn); const bu=(window.ENV?.REACT_APP_BACKEND_URL||process.env.REACT_APP_BACKEND_URL||window.location.origin); const target=f[0]||'1ST FLOOR'; const rooms=(project?.rooms||[]).filter(r=>(r.floor||'1ST FLOOR')===fn); await Promise.all(rooms.map(r=>fetch(`${bu}/api/rooms/${r.id}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({floor:target})}))); await saveFloorOrder(f); if(onReload) await onReload(); };
  const renameFloor = async (old, nw) => { if(!nw||nw===old) return; const bu=(window.ENV?.REACT_APP_BACKEND_URL||process.env.REACT_APP_BACKEND_URL||window.location.origin); const rooms=(project?.rooms||[]).filter(r=>(r.floor||'1ST FLOOR')===old); await Promise.all(rooms.map(r=>fetch(`${bu}/api/rooms/${r.id}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({floor:nw})}))); await saveFloorOrder(getFloorOrder().map(f=>f===old?nw:f)); if(onReload) await onReload(); };
  /** Rooms in render order: grouped by floor, add-order preserved within each floor. */
  const buildOrderedRooms = () => {
    const allRooms = (filteredProject || project)?.rooms || [];
    const floors = getFloorOrder();
    allRooms.forEach(r => { const f = r.floor || '1ST FLOOR'; if (!floors.includes(f)) floors.push(f); });
    const ordered = [];
    floors.forEach(fn => { allRooms.filter(r => (r.floor || '1ST FLOOR') === fn).forEach(r => ordered.push(r)); });
    return { floors, ordered };
  };

  // Check for URL parameters from extension (action=add-item&source=extension)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'add-item' && params.get('source') === 'extension') {
      const scrapedData = {
        name: params.get('name') || '',
        price: params.get('price') || '',
        sku: params.get('sku') || '',
        size: params.get('size') || '',
        finish_color: params.get('finish') || '',
        finish_image: params.get('finish_image') || '',
        vendor: params.get('vendor') || '',
        url: params.get('link') || '',
        link: params.get('link') || '',
        image_url: params.get('image') || '',
        msrp: params.get('msrp') || '',
        remarks: params.get('remarks') || ''
      };
      
      // Only store if we have actual data
      if (scrapedData.name || scrapedData.sku || scrapedData.price) {
        localStorage.setItem('extensionScrapedData', JSON.stringify(scrapedData));
        console.log('Stored scraper data from URL:', scrapedData);
        
        // Clean URL without reloading
        const cleanUrl = window.location.pathname + '?tab=Checklist';
        window.history.replaceState({}, '', cleanUrl);
      }
    }
  }, []);
  
  // Check for scraped data in localStorage on mount and periodically
  useEffect(() => {
    const checkForScrapedData = () => {
      const data = localStorage.getItem('extensionScrapedData');
      if (data) {
        try {
          const parsed = JSON.parse(data);
          setScraperClipboard(parsed);
          setShowScraperNotification(true);
        } catch (e) {
          // Ignore JSON parse errors for invalid clipboard data
        }
      }
    };
    
    checkForScrapedData();
    // Check every 2 seconds for new scraped data
    const interval = setInterval(checkForScrapedData, 2000);
    return () => clearInterval(interval);
  }, []);
  
  // ✅ Search and Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCarrier, setSelectedCarrier] = useState('');
  
  // 🔗 Load punch list linked items for highlighting
  useEffect(() => {
    const loadLinkedPunchItems = async () => {
      if (!project?.id) return;
      
      try {
        const backendUrl = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
        const response = await fetch(`${backendUrl}/api/punch-list/linked-ffe/${project.id}`);
        if (response.ok) {
          const data = await response.json();
          setLinkedPunchItems(data.linked_ffe_items || {});
          console.log('🔗 Loaded punch list links:', Object.keys(data.linked_ffe_items || {}).length);
        }
      } catch (error) {
        console.error('Failed to load punch list links:', error);
      }
    };
    
    loadLinkedPunchItems();
    // Refresh every 30 seconds
    const interval = setInterval(loadLinkedPunchItems, 30000);
    return () => clearInterval(interval);
  }, [project?.id]);

  // 🔗 Load to-do linked items for highlighting
  useEffect(() => {
    const loadTodoLinkedItems = async () => {
      if (!project?.id) return;
      
      try {
        const backendUrl = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
        const response = await fetch(`${backendUrl}/api/todos/${project.id}`);
        if (response.ok) {
          const data = await response.json();
          const linkedMap = new Map();
          (data.todos || []).forEach(todo => {
            if (todo.linked_ffe_item?.id) {
              // Track completion status of the TO-DO, not the item
              const isComplete = todo.completed || todo.status === 'completed';
              linkedMap.set(todo.linked_ffe_item.id, {
                completed: isComplete,
                status: todo.status
              });
            }
          });
          setTodoLinkedItems(linkedMap);
          console.log('📋 FFE: Found', linkedMap.size, 'items linked to To-Do');
        }
      } catch (error) {
        console.error('Failed to load to-do links:', error);
      }
    };
    
    loadTodoLinkedItems();
    const interval = setInterval(loadTodoLinkedItems, 30000);
    return () => clearInterval(interval);
  }, [project?.id]);
  
  // Load photos for all rooms from walkthrough
  useEffect(() => {
    const loadRoomPhotos = async () => {
      if (!project?.id || !project?.rooms) return;
      
      const backendUrl = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
      const photosData = {};
      
      for (const room of project.rooms) {
        try {
          const response = await fetch(`${backendUrl}/api/photos/by-room-name/${project.id}?name=${encodeURIComponent(room.name)}`);
          if (response.ok) {
            const data = await response.json();
            photosData[room.id] = data.photos || [];
          } else {
            const fallbackResponse = await fetch(`${backendUrl}/api/photos/by-room/${project.id}/${room.id}`);
            if (fallbackResponse.ok) {
              const fallbackData = await fallbackResponse.json();
              photosData[room.id] = fallbackData.photos || [];
            } else {
              photosData[room.id] = [];
            }
          }
        } catch (error) {
          console.warn(`Failed to load photos for room ${room.name}:`, error);
          photosData[room.id] = [];
        }
      }
      
      setRoomPhotos(photosData);
      console.log('📸 FF&E: Loaded room photos:', Object.keys(photosData).length, 'rooms');
    };

    loadRoomPhotos();
  }, [project?.id, project?.rooms]);
  
  // Toggle photo folder
  const togglePhotoFolder = (roomId) => {
    setExpandedPhotoRooms(prev => ({ ...prev, [roomId]: !prev[roomId] }));
  };

  // ACTUAL API CALLS - WITH PROPER ERROR HANDLING
  const handleStatusChange = async (itemId, newStatus) => {
    console.log('🔄 Status change request:', { itemId, newStatus });
    
    // Save scroll position before update
    const scrollY = window.scrollY || window.pageYOffset;
    console.log('💾 Saving scroll position:', scrollY);
    
    const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
    
    try {
      const response = await fetch(`${backendUrl}/api/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      console.log('📡 Status change response:', response.status, response.statusText);
      
      if (response.ok) {
        console.log('✅ Status updated successfully');
        
        // AUTO-COMPLETE TO-DO when status changes to completion states
        const completionStatuses = ['ORDERED', 'RECEIVED', 'INSTALLED', 'COMPLETE', 'DELIVERED', 'SAMPLES ARRIVED', 'PICKED'];
        if (completionStatuses.includes(newStatus.toUpperCase())) {
          // Find and complete any linked To-Dos for this item
          try {
            const todosRes = await fetch(`${backendUrl}/api/todos/${project?.id}`);
            if (todosRes.ok) {
              const todosData = await todosRes.json();
              const todos = todosData.todos || [];
              
              // Find todos linked to this item
              for (const todo of todos) {
                if (todo.linked_ffe_item?.id === itemId && !todo.completed) {
                  // Mark this to-do as completed
                  await fetch(`${backendUrl}/api/todos/${todo.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ completed: true, status: 'completed' })
                  });
                  console.log('✅ Auto-completed To-Do:', todo.text);
                  toast.success(`✅ To-Do Completed: ${todo.text}`, {
                    description: `Status changed to ${newStatus}`,
                    duration: 3000,
                  });
                }
              }
            }
          } catch (todoError) {
            console.error('❌ Error auto-completing To-Dos:', todoError);
          }
        }
        
        // Update local state to avoid scroll jump - create proper deep copy
        const updatedProject = JSON.parse(JSON.stringify(filteredProject));
        let itemFound = false;
        
        updatedProject.rooms?.forEach(room => {
          room.categories?.forEach(category => {
            category.subcategories?.forEach(subcategory => {
              subcategory.items?.forEach(item => {
                if (item.id === itemId) {
                  item.status = newStatus;
                  itemFound = true;
                  console.log('✅ Updated item status in local state:', item.name, '→', newStatus);
                }
              });
            });
          });
        });
        
        if (itemFound) {
          setFilteredProject(updatedProject);
          // Restore scroll position after state update
          setTimeout(() => {
            window.scrollTo(0, scrollY);
            console.log('📜 Restored scroll position:', scrollY);
          }, 0);
        } else {
          console.warn('⚠️ Item not found in local state, calling onReload');
          if (onReload) {
            onReload();
          }
          // Restore scroll position after reload
          setTimeout(() => {
            window.scrollTo(0, scrollY);
            console.log('📜 Restored scroll position after reload:', scrollY);
          }, 100);
        }
      } else {
        const errorData = await response.text();
        console.error('❌ Status update failed:', response.status, errorData);
        alert(`Failed to update status: ${response.status} ${response.statusText}\n${errorData}`);
      }
    } catch (error) {
      console.error('❌ Status update error:', error);
      alert(`Error updating status: ${error.message}`);
    }
  };

  const handleCarrierChange = async (itemId, newCarrier) => {
    console.log('🔄 Carrier change request:', { itemId, newCarrier });
    
    try {
      const response = await fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin}/api/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ carrier: newCarrier })
      });
      
      if (response.ok) {
        console.log('✅ Carrier updated successfully');
        
        // Update local state to avoid scroll jump - create proper deep copy
        const updatedProject = JSON.parse(JSON.stringify(filteredProject));
        let itemFound = false;
        
        updatedProject.rooms?.forEach(room => {
          room.categories?.forEach(category => {
            category.subcategories?.forEach(subcategory => {
              subcategory.items?.forEach(item => {
                if (item.id === itemId) {
                  item.carrier = newCarrier;
                  itemFound = true;
                  console.log('✅ Updated item carrier in local state:', item.name, '→', newCarrier);
                }
              });
            });
          });
        });
        
        if (itemFound) {
          setFilteredProject(updatedProject);
        } else {
          console.warn('⚠️ Item not found in local state, calling onReload');
          if (onReload) {
            onReload();
          }
        }
      } else {
        const errorData = await response.text();
        console.error('❌ Carrier update failed:', response.status, errorData);
        alert(`Failed to update carrier: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Carrier update error:', error);
      alert(`Error updating carrier: ${error.message}`);
    }
  };

  // Handle updating any item field (vendor, quantity, size, finish_color, cost, etc.)
  const handleUpdateItemField = async (itemId, field, value) => {
    console.log('🔄 FFE: Updating item field:', { itemId, field, value });
    
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      
      const response = await fetch(`${backendUrl}/api/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value })
      });
      
      if (response.ok) {
        console.log(`✅ FFE: Item ${field} updated successfully to:`, value);
        
        // If finish_color was updated, sync to Material Libraries
        if (field === 'finish_color' && value && value.trim()) {
          // Find the item to get vendor, name, sku info
          let itemData = null;
          filteredProject?.rooms?.forEach(room => {
            room.categories?.forEach(category => {
              category.subcategories?.forEach(subcategory => {
                subcategory.items?.forEach(item => {
                  if (item.id === itemId) {
                    itemData = item;
                  }
                });
              });
            });
          });
          
          if (itemData) {
            // Sync to Material Libraries (with duplicate check)
            try {
              const materialData = {
                name: value.trim(),
                category: 'finish',
                manufacturer: itemData.vendor || '',
                vendor: itemData.vendor || '',
                sku: itemData.sku ? `${itemData.sku}-FINISH` : '',
                color: value.trim(),
                photo_url: itemData.finish_image || '',
                notes: `From product: ${itemData.name || 'Unknown'}`,
                tags: ['scraped', 'auto-added', itemData.vendor || ''].filter(Boolean).join(','),
                project_id: project?.id || null
              };
              
              // Add to Global Master Materials (with duplicate check)
              await fetch(`${backendUrl}/api/materials/from-scraper`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(materialData)
              });
              console.log('📚 FFE: Synced finish to Global Materials Library:', value);
              
              // Add to Project Materials (with duplicate check)
              if (project?.id) {
                await fetch(`${backendUrl}/api/materials`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(materialData)
                });
                console.log('📚 FFE: Synced finish to Project Materials Library:', value);
              }
            } catch (materialErr) {
              console.warn('⚠️ FFE: Could not sync to Materials Library:', materialErr);
            }
          }
        }
        
        // Update local state
        const updatedProject = JSON.parse(JSON.stringify(filteredProject));
        updatedProject.rooms?.forEach(room => {
          room.categories?.forEach(category => {
            category.subcategories?.forEach(subcategory => {
              subcategory.items?.forEach(item => {
                if (item.id === itemId) {
                  item[field] = value;
                }
              });
            });
          });
        });
        setFilteredProject(updatedProject);
      } else {
        console.error(`❌ FFE: Failed to update item ${field}:`, response.status);
      }
    } catch (error) {
      console.error(`❌ FFE: Error updating item ${field}:`, error);
    }
  };

  // Manual finish pick: let the user attach/replace a finish swatch by hand for
  // items whose tear sheet had no detectable swatch.
  const handleFinishUpload = async (itemId, file) => {
    if (!file) return;
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('item_id', itemId);
      fd.append('image_type', 'finish');
      const res = await fetch(`${backendUrl}/api/upload-item-image`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success && data.image_url) {
        const updated = JSON.parse(JSON.stringify(filteredProject));
        updated.rooms?.forEach(room => room.categories?.forEach(cat => cat.subcategories?.forEach(sc => sc.items?.forEach(it => { if (it.id === itemId) it.finish_image = data.image_url; }))));
        setFilteredProject(updated);
      } else {
        alert('Finish upload failed');
      }
    } catch (e) {
      console.error('❌ FFE: finish upload failed', e);
      alert('Finish upload failed');
    }
  };

  // APPLY FILTERS - SIMPLE WORKING VERSION
  useEffect(() => {
    console.log('🔍 Filter triggered:', { searchTerm, selectedRoom, selectedCategory, selectedVendor, selectedStatus, selectedCarrier });
    
    if (!project) {
      setFilteredProject(null);
      return;
    }

    let filtered = { ...project };

    // Apply filters if any are selected
    if (searchTerm || selectedRoom || selectedCategory || selectedVendor || selectedStatus || selectedCarrier) {
      console.log('🔍 Applying filters...');
      
      filtered.rooms = project.rooms.map(room => {
        // Room filter
        if (selectedRoom && room.id !== selectedRoom) {
          return { ...room, categories: [] }; // Hide room content but keep room header
        }
        
        // Filter categories and items
        const filteredCategories = room.categories.map(category => {
          // Category filter
          if (selectedCategory && category.name.toLowerCase() !== selectedCategory.toLowerCase()) {
            return { ...category, subcategories: [] };
          }
          
          // Filter subcategories and items
          const filteredSubcategories = category.subcategories.map(subcategory => {
            const filteredItems = subcategory.items.filter(item => {
              // Search term filter
              if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const itemMatch = 
                  item.name.toLowerCase().includes(searchLower) ||
                  (item.vendor && item.vendor.toLowerCase().includes(searchLower)) ||
                  (item.sku && item.sku.toLowerCase().includes(searchLower));
                if (!itemMatch) return false;
              }
              
              // Vendor filter
              if (selectedVendor && item.vendor !== selectedVendor) {
                return false;
              }
              
              // Status filter
              if (selectedStatus && item.status !== selectedStatus) {
                return false;
              }
              
              // Carrier filter
              if (selectedCarrier && item.carrier !== selectedCarrier) {
                return false;
              }
              
              return true;
            });
            
            return { ...subcategory, items: filteredItems };
          });
          
          return { ...category, subcategories: filteredSubcategories };
        });
        
        return { ...room, categories: filteredCategories };
      });
    }

    setFilteredProject(filtered);
    console.log('🔍 Filter applied, rooms:', filtered.rooms.length);
  }, [project, searchTerm, selectedRoom, selectedCategory, selectedVendor, selectedStatus, selectedCarrier]);

  // Load available categories on component mount
  useEffect(() => {
    const loadAvailableCategories = async () => {
      try {
        const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
        const response = await fetch(`${backendUrl}/api/categories/available`);
        if (response.ok) {
          const data = await response.json();
          setAvailableCategories(data.categories || []);
        }
      } catch (error) {
        console.error('❌ Error loading available categories:', error);
        setAvailableCategories([
          // Every name here is verified against /api/categories/available.
          // The previous fallback listed categories the server had never heard
          // of, which is the fault Neil reported: the add appeared to work but
          // produced an empty category.
          "Lighting", "Furniture", "Appliances", "Plumbing", "Flooring",
          "Countertops", "Textiles", "Cabinets & Countertops",
          "Paint & Finishes", "Window Treatments", "Art & Accessories"
        ]);
      }
    };
    
    loadAvailableCategories();
    
    // Default NEW rooms/categories to expanded, but NEVER override the user's
    // existing expand/collapse choices. This runs on every project refresh
    // (10s background poll), so overwriting here would reset the UI — instead
    // we only fill in ids we haven't seen before.
    if (project?.rooms) {
      setExpandedRooms(prev => {
        const next = { ...prev };
        project.rooms.forEach(room => {
          if (!(room.id in next)) next[room.id] = true;
        });
        return next;
      });
      setExpandedCategories(prev => {
        const next = { ...prev };
        project.rooms.forEach(room => {
          room.categories?.forEach(category => {
            if (!(category.id in next)) next[category.id] = true;
          });
        });
        return next;
      });
    }
  }, [project]);

  // Handle scraping product information
  const handleScrapeProduct = async (productLink, itemId) => {
    if (!productLink?.trim()) {
      alert('Please enter a product URL first');
      return;
    }

    try {
      console.log('🔍 FFE: Scraping product from:', productLink);
      
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const response = await fetch(`${backendUrl}/api/scrape-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: productLink })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ FFE: Scraping successful:', result);

        // Update the item with scraped data
        const updateData = {
          ...result.data,
          link: productLink // Ensure link is preserved
        };

        // Remove fields we don't want to overwrite if they're empty
        if (!updateData.name) delete updateData.name;
        
        const updateResponse = await fetch(`${backendUrl}/api/items/${itemId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateData)
        });

        if (updateResponse.ok) {
          console.log('✅ FFE: Item updated with scraped data');
          alert(`✅ Successfully scraped: ${result.data.name || 'Product information'}`);
          if (onReload) {
            onReload();
          }
        } else {
          console.error('❌ FFE: Failed to update item with scraped data');
          alert('❌ Failed to update item with scraped data');
        }
      } else {
        const errorData = await response.json();
        console.error('❌ FFE: Scraping failed:', errorData);
        alert(`❌ Scraping failed: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ FFE: Scraping error:', error);
      alert(`❌ Error during scraping: ${error.message}`);
    }
  };

  const _backendBase = () => (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);

  // Pull ONE item's photo fresh from its vendor link (image only, nothing else).
  const handleRefreshItemPhoto = async (item) => {
    setRefreshingItemId(item.id);
    try {
      const res = await fetch(`${_backendBase()}/api/items/${item.id}/refresh-photo`, { method: 'POST' });
      const data = await res.json();
      if (data.success) { if (onReload) await onReload(); }
      else { alert(data.message || 'No usable image found.'); }
    } catch (e) {
      alert('Could not find photo: ' + e.message);
    } finally {
      setRefreshingItemId(null);
    }
  };

  // Generate a client-ready description + specs for one item and save it.
  const handleDescribeItem = async (item) => {
    setDescribingItemId(item.id);
    try {
      const res = await fetch(`${_backendBase()}/api/ai-studio/item-description`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: item.name || '', vendor: item.vendor || '',
          room: item.room_location || '', link: item.link || item.product_link || '',
        }),
      });
      const d = await res.json();
      if (res.ok && d.description) {
        await handleUpdateItemField(item.id, 'description', d.description);
        toast.success('✨ Description generated');
      } else {
        alert(d.detail || 'Could not generate a description.');
      }
    } catch (e) {
      alert('Describe failed: ' + e.message);
    } finally {
      setDescribingItemId(null);
    }
  };

  // MASS STATUS CHANGE — set every currently-shown item to one status. Respects
  // the active filters above, so you can narrow it down first.
  const gatherVisibleItemIds = () => {
    const ids = [];
    ((filteredProject || project)?.rooms || []).forEach((r) =>
      (r.categories || []).forEach((c) =>
        (c.subcategories || []).forEach((s) =>
          (s.items || []).forEach((it) => it?.id && ids.push(it.id)))));
    return ids;
  };

  const toggleItemSelect = (id) => setSelectedItemIds((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const clearSelection = () => setSelectedItemIds(new Set());
  const selectAllShown = () => setSelectedItemIds(new Set(gatherVisibleItemIds()));

  const handleBulkStatus = async () => {
    if (!bulkStatus) { alert('Pick a status first.'); return; }
    const usingSelection = selectedItemIds.size > 0;
    const ids = usingSelection ? Array.from(selectedItemIds) : gatherVisibleItemIds();
    if (!ids.length) { alert('No items to update.'); return; }
    const scope = usingSelection ? `${ids.length} selected` : `ALL ${ids.length} shown`;
    if (!window.confirm(`Set ${scope} item(s) to "${bulkStatus}"?${usingSelection ? '' : '\n\nTip: tick the checkboxes to hand-pick items, or use the filters above to narrow this down.'}`)) return;
    setBulkBusy(true);
    try {
      const res = await fetch(`${_backendBase()}/api/items/bulk-status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_ids: ids, status: bulkStatus }),
      });
      const d = await res.json();
      toast.success(`Updated ${d.updated} item(s) to ${bulkStatus}`);
      setBulkStatus('');
      clearSelection();
      if (onReload) await onReload();
    } catch (e) {
      alert('Bulk update failed: ' + e.message);
    } finally {
      setBulkBusy(false);
    }
  };

  // Bulk: pull higher-res photos for every item that has a vendor link.
  const handleRefreshAllPhotos = async () => {
    if (!project?.id) return;
    if (!window.confirm("Pull a fresh, higher-res photo from each item's vendor link and replace the current thumbnail? Items without a link are skipped.")) return;
    setRefreshingPhotos(true);
    setPhotoProgress({ done: 0, total: 0, updated: 0 });
    try {
      const start = await fetch(`${_backendBase()}/api/projects/${project.id}/refresh-product-photos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ overwrite: true }),
      });
      const { job_id } = await start.json();
      let finished = false;
      while (!finished) {
        await new Promise((r) => setTimeout(r, 1500));
        const st = await fetch(`${_backendBase()}/api/projects/${project.id}/refresh-product-photos/${job_id}`);
        const job = await st.json();
        setPhotoProgress({ done: job.done || 0, total: job.total || 0, updated: job.updated || 0 });
        if (job.status === 'done' || job.status === 'error') {
          finished = true;
          if (job.status === 'error') alert('Photo refresh hit an error: ' + (job.error || 'unknown'));
          else alert(`Done — updated ${job.updated} of ${job.total} item photo${job.total === 1 ? '' : 's'}. (Items whose vendor blocks automated fetching keep their current photo.)`);
          if (onReload) await onReload();
        }
      }
    } catch (e) {
      alert('Could not refresh photos: ' + e.message);
    } finally {
      setRefreshingPhotos(false);
      setPhotoProgress(null);
    }
  };


  // ✅ PASTE SCRAPED DATA INTO A SPECIFIC ITEM ROW
  const handlePasteScrapedData = async (itemId) => {
    if (!scraperClipboard) {
      alert('No scraped data available. Use the Chrome extension to scrape a product first!');
      return;
    }
    
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      
      // Prepare update data from scraped clipboard
      const updateData = {
        vendor: scraperClipboard.vendor || undefined,
        sku: scraperClipboard.sku || undefined,
        cost: scraperClipboard.price ? parseFloat(scraperClipboard.price) : undefined,
        size: scraperClipboard.size || undefined,
        finish_color: scraperClipboard.finish_color || undefined,
        finish_image: scraperClipboard.finish_image || undefined,
        image_url: scraperClipboard.image_url || undefined,
        link: scraperClipboard.link || scraperClipboard.url || undefined,
        remarks: scraperClipboard.remarks || undefined
      };
      
      // Remove undefined values
      Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key]);
      
      console.log('📋 Pasting scraped data into item:', itemId, updateData);
      
      const response = await fetch(`${backendUrl}/api/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        // Clear the clipboard after successful paste
        localStorage.removeItem('extensionScrapedData');
        setScraperClipboard(null);
        setShowScraperNotification(false);
        setSelectedItemForPaste(null);
        
        alert(`✅ Pasted: ${scraperClipboard.name || 'Product data'}\n\nVendor: ${updateData.vendor || 'N/A'}\nPrice: $${updateData.cost || 'N/A'}\nFinish: ${updateData.finish_color || 'N/A'}`);
        
        if (onReload) {
          onReload();
        }
      } else {
        alert('❌ Failed to paste data. Please try again.');
      }
    } catch (error) {
      console.error('❌ Paste error:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };
  
  // Clear the scraper clipboard
  const clearScraperClipboard = () => {
    localStorage.removeItem('extensionScrapedData');
    setScraperClipboard(null);
    setShowScraperNotification(false);
  };

  // Handle adding new items - FIX THE SUBCATEGORY SELECTION
  const handleAddItem = async (itemData) => {
    try {
      // Find the first available subcategory if none selected
      let subcategoryId = selectedSubCategoryId;
      
      if (!subcategoryId) {
        // Find the first subcategory from any expanded room/category
        for (const room of project.rooms) {
          if (expandedRooms[room.id]) {
            for (const category of room.categories || []) {
              if (expandedCategories[category.id] && category.subcategories?.length > 0) {
                subcategoryId = category.subcategories[0].id;
                console.log(`🔍 Auto-selected subcategory: ${category.subcategories[0].name}`);
                break;
              }
            }
            if (subcategoryId) break;
          }
        }
      }

      if (!subcategoryId) {
        alert('Please expand a category first to add items to it.');
        return;
      }

      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      
      const newItem = {
        ...itemData,
        subcategory_id: subcategoryId,
        order_index: 0
      };

      console.log('📤 Creating item:', newItem);

      const response = await fetch(`${backendUrl}/api/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newItem)
      });

      if (response.ok) {
        console.log('✅ Item added successfully');
        setShowAddItem(false);
        // Use onReload prop instead of window.location.reload to prevent page jump
        if (onReload) {
          onReload();
        }
      } else {
        const errorData = await response.text();
        console.error('❌ Backend error:', errorData);
        throw new Error(`HTTP ${response.status}: ${errorData}`);
      }
    } catch (error) {
      console.error('❌ Error adding item:', error);
      alert(`Failed to add item: ${error.message}`);
    }
  };

  // Handle deleting a room
  const handleDeleteRoom = async (roomId) => {
    if (!roomId) {
      console.error('❌ No room ID provided');
      return;
    }

    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const response = await fetch(`${backendUrl}/api/rooms/${roomId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        if (onReload) onReload();
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error deleting room:', error);
    }
  };

  // Handle deleting an item - NO RELOAD
  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const response = await fetch(`${backendUrl}/api/items/${itemId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        console.log('✅ Item deleted successfully');
        alert('Item deleted successfully!');
        // Force reload to show updated data
        if (onReload) {
          onReload();
        }
      } else {
        console.error('❌ Delete failed with status:', response.status);
        alert(`Delete failed: ${response.status}`);
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error deleting item:', error);
      alert(`Delete error: ${error.message}`);
      alert('Failed to delete item. Please try again.');
    }
  };

  // Handle adding a new room - SIMPLE VERSION LIKE BEFORE
  const handleAddRoom = () => {
    if (onAddRoom) {
      onAddRoom();
    }
  };

  // Handle adding a new category WITH ALL SUBCATEGORIES AND INSTALLEDS
  const handleAddSubcategory = async (categoryId, subcategoryName) => {
    if (!categoryId || !subcategoryName) return;
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const res = await fetch(`${backendUrl}/api/subcategories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: subcategoryName, category_id: categoryId, order_index: 999 })
      });
      if (res.ok) {
        if (onReload) onReload();
      } else {
        alert('Failed to add sub-category');
      }
    } catch (err) {
      console.error('❌ Error adding sub-category:', err);
      alert('Failed to add sub-category');
    }
  };

  const handleAddCategory = async (roomId, categoryName) => {
    if (!roomId || !categoryName) {
      console.error('❌ Missing roomId or categoryName');
      return;
    }

    // This used to create a THROWAWAY ROOM named after the category, in a fake
    // project called "temp", purely so the server would auto-generate a
    // structure it could copy the category out of. That could not work: the
    // auto-generated structure is keyed off the ROOM name, and a room called
    // "temp_Cabinets & Storage_1758..." is not a room type, so the server
    // returned a generic seven-category default. Anything outside those seven
    // -- and every brand-new name -- silently fell through a bare `if (found)`
    // with no error, which is why "add category" appeared to do nothing at all.
    //
    // /api/categories/comprehensive does the job directly, and is what the
    // Checklist and Walkthrough sheets were already calling.
    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const response = await fetch(
        `${backendUrl}/api/categories/comprehensive?room_id=${roomId}&category_name=${encodeURIComponent(categoryName)}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      const newCategory = await response.json();
      const subCount = newCategory.subcategories?.length || 0;
      console.log(`✅ FFE: added category "${newCategory.name}" with ${subCount} subcategories`);

      if (onReload) onReload();
    } catch (error) {
      // Say so. The old code failed silently, which is the whole complaint.
      console.error('❌ FFE: failed to add category:', error);
      alert(`Could not add "${categoryName}". Please try again — nothing was changed.`);
    }
  };

  // Drag and drop functionality removed

  // Handle drag and drop for rooms and categories
  const handleDragEnd = async (result) => {
    console.log('🎯 FFE DRAG END CALLED!', result);
    if (!result.destination) {
      console.log('❌ No destination');
      return;
    }

    const { source, destination, type } = result;

    try {
      if (type === 'room') {
        // Create deep copy of project
        const updatedProject = {...project};
        // Drag indices come from the rendered (floor-grouped) order, so reorder
        // that list - not project.rooms, whose order differs once floors group.
        const newRooms = Array.from(buildOrderedRooms().ordered);
        const [removed] = newRooms.splice(source.index, 1);
        newRooms.splice(destination.index, 0, removed);

        // A room dropped into another floor's group must ADOPT that floor.
        // The rendered list is floor-grouped, so the new floor is whatever the
        // neighbour at the drop position sits on.
        const prevRoom = newRooms[destination.index - 1];
        const nextRoom = newRooms[destination.index + 1];
        const targetFloor = (prevRoom && (prevRoom.floor || '1ST FLOOR'))
                         || (nextRoom && (nextRoom.floor || '1ST FLOOR'))
                         || (removed.floor || '1ST FLOOR');
        const floorChanged = (removed.floor || '1ST FLOOR') !== targetFloor;
        removed.floor = targetFloor;

        updatedProject.rooms = newRooms;

        console.log('🔄 FFE: Moving room from', source.index, 'to', destination.index, 'floor →', targetFloor);
        console.log('📦 FFE: New room order:', newRooms.map(r => r.name));

        // Force React to re-render
        setFilteredProject(updatedProject);

        // Persist the new order for every room, plus the new floor for the one
        // that moved. Reload afterwards when the floor changed so the grouping
        // re-renders from server truth.
        Promise.all(newRooms.map((room, i) =>
          fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin}/api/rooms/${room.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(room.id === removed.id ? { order_index: i, floor: targetFloor } : { order_index: i })
          })
        )).then(() => { if (floorChanged && onReload) onReload(); });

        console.log('✅ FFE: Rooms reordered!');
      } else if (type === 'category') {
        // Create deep copy of project
        const updatedProject = {...project};
        const roomId = source.droppableId.replace('categories-', '');
        const room = updatedProject.rooms.find(r => r.id === roomId);
        if (!room) return;

        const newCategories = Array.from(room.categories);
        const [removed] = newCategories.splice(source.index, 1);
        newCategories.splice(destination.index, 0, removed);
        
        room.categories = newCategories;
        
        console.log('🔄 FFE: Moving category from', source.index, 'to', destination.index);
        
        // Force React to re-render
        setFilteredProject(updatedProject);

        // Update backend silently
        Promise.all(newCategories.map((category, i) => 
          fetch(`${(window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin}/api/categories/${category.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_index: i })
          })
        ));

        console.log('✅ FFE: Categories reordered!');
      } else if (type === 'item') {
        // Move an item to another category/room by drag. Reordering WITHIN a
        // category isn't persisted by the API (items read back by created_at),
        // so a same-category drop is a no-op; cross-category drops relocate it.
        const itemId = result.draggableId;
        const srcCatId = source.droppableId.replace('items-', '');
        const dstCatId = destination.droppableId.replace('items-', '');
        if (srcCatId === dstCatId) return;
        let dstCat = null;
        for (const r of (project.rooms || [])) {
          const c = (r.categories || []).find(cc => cc.id === dstCatId);
          if (c) { dstCat = c; break; }
        }
        const targetSubId = dstCat?.subcategories?.[0]?.id;
        if (!targetSubId) { alert('That category has no section to drop into yet.'); return; }
        const backendUrl = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
        const resp = await fetch(`${backendUrl}/api/items/${itemId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subcategory_id: targetSubId })
        });
        if (resp.ok) {
          if (onReload) onReload();
        } else {
          alert('Failed to move item');
        }
      }
    } catch (error) {
      console.error('❌ Drag error:', error);
    }
  };

  // Native drag-and-drop for ITEMS. The nested react-beautiful-dnd droppables
  // can't measure a drop zone inside this single-table layout, so items use
  // reliable HTML5 drag/drop: drag an item's handle onto any row of another
  // section to move it there.
  const dragItemRef = useRef(null);
  const onItemDragStart = (e, item) => {
    dragItemRef.current = { id: item.id, subId: item.subcategoryId };
    try {
      const tr = e.currentTarget.closest('tr');
      if (tr) e.dataTransfer.setDragImage(tr, 20, 20);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', item.id);
    } catch (_) {}
  };
  const dragOverIfItem = (e) => { if (dragItemRef.current) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } };
  const onItemDropToSub = async (e, targetSubId) => {
    e.preventDefault();
    const drag = dragItemRef.current;
    dragItemRef.current = null;
    if (!drag || !targetSubId || drag.subId === targetSubId) return;
    try {
      const updated = JSON.parse(JSON.stringify(filteredProject));
      let moved = null;
      updated.rooms?.forEach(r => r.categories?.forEach(c => c.subcategories?.forEach(sc => {
        const idx = (sc.items || []).findIndex(x => x.id === drag.id);
        if (idx >= 0) moved = sc.items.splice(idx, 1)[0];
      })));
      if (moved) {
        moved.subcategory_id = targetSubId;
        updated.rooms?.forEach(r => r.categories?.forEach(c => c.subcategories?.forEach(sc => {
          if (sc.id === targetSubId) sc.items = [...(sc.items || []), moved];
        })));
        setFilteredProject(updated);
      }
    } catch (_) {}
    try {
      const backendUrl = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
      const resp = await fetch(`${backendUrl}/api/items/${drag.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subcategory_id: targetSubId }),
      });
      if (!resp.ok && onReload) onReload();
    } catch (err) { console.error('item move failed', err); if (onReload) onReload(); }
  };

  // Toggle room expansion
  const toggleRoomExpansion = (roomId) => {
    setExpandedRooms(prev => {
      const newState = {
        ...prev,
        [roomId]: !prev[roomId]
      };
      localStorage.setItem('ffe_expandedRooms', JSON.stringify(newState));
      return newState;
    });
  };

  // Toggle category expansion
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const newState = {
        ...prev,
        [categoryId]: !prev[categoryId]
      };
      localStorage.setItem('ffe_expandedCategories', JSON.stringify(newState));
      return newState;
    });
  };

  // Handle tracking items
  const handleTrackItem = async (item) => {
    if (!item.tracking_number) {
      console.error('❌ No tracking number available');
      return;
    }

    try {
      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
      const response = await fetch(`${backendUrl}/api/track-shipment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tracking_number: item.tracking_number,
          carrier: item.carrier || 'auto-detect'
        })
      });

      if (response.ok) {
        const trackingData = await response.json();
        console.log('✅ Tracking data retrieved');
      } else {
        console.error('❌ Failed to get tracking information');
      }
    } catch (error) {
      console.error('❌ Tracking error:', error);
    }
  };

  const getMainHeaderColor = () => '#8B4444';  // Dark red for main headers
  const getAdditionalInfoColor = () => '#8B4444';  // Brown for ADDITIONAL INFO.
  const getShippingInfoColor = () => '#6B21A8';  // Purple for SHIPPING INFO.
  const getNotesActionsColor = () => '#8B4444';  // Red for NOTES and ACTIONS

  // Get carrier color to match header colors
  const getCarrierColor = (carrier) => {
    const colors = {
      'FedEx': '#FF6600',           // FedEx Orange
      'FedEx Ground': '#FF6600',    // FedEx Orange
      'FedEx Express': '#FF6600',   // FedEx Orange
      'UPS': '#8B4513',            // UPS Brown
      'UPS Ground': '#8B4513',     // UPS Brown
      'UPS Express': '#8B4513',    // UPS Brown
      'USPS': '#004B87',           // USPS Blue
      'DHL': '#FFD700',            // DHL Yellow
      'Brooks': '#4682B4',         // Steel Blue
      'Zenith': '#20B2AA',         // Light Sea Green
      'Sunbelt': '#FF4500',        // Orange Red
      'R+L Carriers': '#32CD32',   // Lime Green
      'Yellow Freight': '#FFD700', // Yellow
      'XPO Logistics': '#6A5ACD',  // Slate Blue
      'Old Dominion': '#DC143C',   // Crimson
      'ABF Freight': '#FF6347',    // Tomato
      'Con-Way': '#48D1CC',        // Medium Turquoise
      'Estes Express': '#9370DB',  // Medium Purple
      'YRC Freight': '#FF1493',    // Deep Pink
      'Saia': '#00CED1',           // Dark Turquoise
      'OTHER': '#808080'           // Gray
    };
    return colors[carrier] || '#6B7280';
  };

  const getShipToColor = (shipTo) => {
    const shipToColors = {
      'CLIENT HOME': '#4169E1',
      'JOB SITE': '#228B22',
      'DESIGN CENTER': '#FF8C00',
      'WAREHOUSE': '#8B4513',
      'VENDOR LOCATION': '#9370DB'
    };
    return shipToColors[shipTo] || '#FEF08A';
  };

  const getStockStatusColor = (stockStatus) => {
    const stockColors = {
      'IN STOCK': '#10B981',        // Green
      'LOW STOCK': '#F59E0B',       // Orange
      'OUT OF STOCK': '#EF4444',    // Red
      'BACKORDERED': '#DC2626',     // Dark Red
      'DISCONTINUED': '#991B1B'     // Very Dark Red
    };
    return stockColors[stockStatus] || 'transparent';
  };

  const getDeliveryStatusColor = (status) => {
    const deliveryColors = {
      'PENDING': '#FEF08A',
      'SCHEDULED': '#BEF264',
      'PROCESSING': '#FDE047',
      'IN TRANSIT': '#FACC15',
      'OUT FOR DELIVERY': '#A3E635',
      'ATTEMPTED DELIVERY': '#F87171',
      'DELIVERED': '#4ADE80',
      'DELIVERED TO RECEIVER': '#22C55E',
      'AVAILABLE FOR PICKUP': '#16A34A',
      'DELAYED': '#EF4444',
      'EXCEPTION': '#DC2626',
      'DAMAGED': '#B91C1C',
      'LOST': '#991B1B',
      'RETURNED TO SENDER': '#8B4444'
    };
    return deliveryColors[status] || '#FEF08A';
  };

  // Still fetching the project -> genuine loading state.
  if (!project) {
    return (
      <div className="text-center text-gray-400 py-8">
        <p className="text-lg">Loading FF&E data...</p>
        <p className="text-sm mt-2">Please wait while we load your project information.</p>
      </div>
    );
  }

  // Project loaded, but the FF&E sheet has no rooms yet (nothing transferred
  // from the Checklist). Show a clear empty state instead of a stuck "Loading".
  if (!project.rooms || project.rooms.length === 0) {
    return (
      <div className="text-center text-gray-400 py-10">
        <p className="text-lg text-[#D4A574] font-semibold">No FF&E items yet</p>
        <p className="text-sm mt-2">Transfer checked items from the Checklist into FF&E, or add a room to start building the FF&E sheet.</p>
        {onAddRoom && (
          <button
            onClick={onAddRoom}
            className="mt-4 px-6 py-3 bg-amber-700 hover:bg-amber-600 text-white rounded font-medium"
          >
            + Add FF&E Room
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full" style={{ backgroundColor: '#0F172A' }}>
      
      {/* SEARCH AND FILTER SECTION - EXACTLY LIKE YOUR SCREENSHOT */}
      <div className="mb-6 p-4" style={{ backgroundColor: '#1E293B' }}>
        <div className="flex flex-col lg:flex-row gap-4 items-center">
          {/* Search Input */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search Items, Vendors, SKUs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-[#B49B7E] text-white focus:outline-none placeholder-[#D4C5A9]/70"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
                boxShadow: '0 0 20px rgba(212, 165, 116, 0.3), inset 0 0 30px rgba(212, 165, 116, 0.08)'
              }}
            />
          </div>
          
          {/* Filter Dropdowns */}
          <div className="flex gap-3 flex-wrap">
            <select 
              value={selectedRoom}
              onChange={(e) => setSelectedRoom(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#B49B7E] text-[#D4C5A9] focus:outline-none"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
                boxShadow: '0 0 15px rgba(212, 165, 116, 0.2), inset 0 0 25px rgba(212, 165, 116, 0.06)'
              }}
            >
              <option value="">All Rooms</option>
              {project.rooms.map(room => (
                <option key={room.id} value={room.id}>{room.name}</option>
              ))}
            </select>
            
            <select 
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#B49B7E] text-[#D4C5A9] focus:outline-none"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
                boxShadow: '0 0 15px rgba(212, 165, 116, 0.2), inset 0 0 25px rgba(212, 165, 116, 0.06)'
              }}
            >
              <option value="">All Categories</option>
              {availableCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            
            <select 
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#B49B7E] text-[#D4C5A9] focus:outline-none"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
                boxShadow: '0 0 15px rgba(212, 165, 116, 0.2), inset 0 0 25px rgba(212, 165, 116, 0.06)'
              }}
            >
              <option value="">All Vendors</option>
              <option value="Visual Comfort">Visual Comfort</option>
              <option value="Four Hands">Four Hands</option>
              <option value="West Elm">West Elm</option>
              <option value="Pottery Barn">Pottery Barn</option>
              <option value="Williams Sonoma">Williams Sonoma</option>
              <option value="Crate & Barrel">Crate & Barrel</option>
              <option value="CB2">CB2</option>
              <option value="Restoration Hardware">Restoration Hardware</option>
            </select>
            
            <select 
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#B49B7E] text-[#D4C5A9] focus:outline-none"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
                boxShadow: '0 0 15px rgba(212, 165, 116, 0.2), inset 0 0 25px rgba(212, 165, 116, 0.06)'
              }}
            >
              <option value="">All Status</option>
              <option value="TO BE SELECTED">TO BE SELECTED</option>
              <option value="RESEARCHING">RESEARCHING</option>
              <option value="PENDING APPROVAL">PENDING APPROVAL</option>
              <option value="APPROVED">APPROVED</option>
              <option value="ORDERED">ORDERED</option>
              <option value="REPLACEMENT">REPLACEMENT</option>
              <option value="CONFIRMED">CONFIRMED</option>
              <option value="IN PRODUCTION">IN PRODUCTION</option>
              <option value="SHIPPED">SHIPPED</option>
              <option value="IN TRANSIT">IN TRANSIT</option>
              <option value="OUT FOR DELIVERY">OUT FOR DELIVERY</option>
              <option value="DELIVERED TO RECEIVER">DELIVERED TO RECEIVER</option>
              <option value="DELIVERED TO JOB SITE">DELIVERED TO JOB SITE</option>
              <option value="RECEIVED">RECEIVED</option>
              <option value="READY FOR INSTALL">READY FOR INSTALL</option>
              <option value="INSTALLING">INSTALLING</option>
              <option value="INSTALLED">INSTALLED</option>
              <option value="ON HOLD">ON HOLD</option>
              <option value="BACKORDERED">BACKORDERED</option>
              <option value="DAMAGED">DAMAGED</option>
              <option value="RETURNED">RETURNED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>
            
            <select 
              value={selectedCarrier}
              onChange={(e) => setSelectedCarrier(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#B49B7E] text-[#D4C5A9] focus:outline-none"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
                boxShadow: '0 0 15px rgba(212, 165, 116, 0.2), inset 0 0 25px rgba(212, 165, 116, 0.06)'
              }}
            >
              <option value="">All Carriers</option>
              <option value="FedEx" style={{ backgroundColor: '#FF6600', color: 'white' }}>FedEx</option>
              <option value="UPS" style={{ backgroundColor: '#8B4513', color: 'white' }}>UPS</option>
              <option value="USPS" style={{ backgroundColor: '#004B87', color: 'white' }}>USPS</option>
              <option value="DHL" style={{ backgroundColor: '#FFD700', color: 'black' }}>DHL</option>
              <option value="Brooks" style={{ backgroundColor: '#4682B4', color: 'white' }}>Brooks</option>
              <option value="Zenith" style={{ backgroundColor: '#20B2AA', color: 'white' }}>Zenith</option>
              <option value="Sunbelt" style={{ backgroundColor: '#FF4500', color: 'white' }}>Sunbelt</option>
              <option value="R+L Carriers" style={{ backgroundColor: '#32CD32', color: 'white' }}>R+L Carriers</option>
            </select>
            
            {/* Filter and Clear Buttons */}
            <button 
              onClick={() => {
                console.log('🔍 FILTER APPLIED - Search:', searchTerm, 'Room:', selectedRoom, 'Category:', selectedCategory, 'Vendor:', selectedVendor, 'Status:', selectedStatus, 'Carrier:', selectedCarrier);
                // Filters are applied automatically via useEffect
              }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
            >
              🔍 FILTER
            </button>
            <button 
              onClick={() => {
                setSearchTerm('');
                setSelectedRoom('');
                setSelectedCategory('');
                setSelectedVendor('');
                setSelectedStatus('');
                setSelectedCarrier('');
                console.log('🧹 FILTER CLEARED');
              }}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium"
            >
              CLEAR
            </button>
          </div>
          
          {/* Bulk Delete Rooms Button */}
          {selectedRoomsForDelete.size > 0 && (
            <button 
              onClick={() => {
                if (onBulkDeleteRooms) {
                  onBulkDeleteRooms(Array.from(selectedRoomsForDelete));
                  setSelectedRoomsForDelete(new Set());
                }
              }}
              className="px-4 py-2 rounded bg-red-700 hover:bg-red-800 text-white font-bold text-sm animate-pulse"
            >
              🗑️ Delete {selectedRoomsForDelete.size} Room{selectedRoomsForDelete.size > 1 ? 's' : ''}
            </button>
          )}
          
          {/* Add Room Button - GOLD/AMBER COLOR */}
          <button 
            onClick={handleAddRoom}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded font-medium"
          >
            ✚ ADD ROOM
          </button>

          {/* Bulk higher-res photo refresh from vendor links */}
          <button
            onClick={handleRefreshAllPhotos}
            disabled={refreshingPhotos}
            data-testid="ffe-refresh-photos-btn"
            title="Fill in missing product photos for every item (from the vendor link, or by name/SKU where a search key is configured)"
            className="px-4 py-2 bg-[#8B7355] hover:bg-[#9B8365] disabled:opacity-60 text-white rounded font-medium"
          >
            {refreshingPhotos ? (photoProgress?.total ? `⏳ ${photoProgress.done || 0}/${photoProgress.total}…` : '⏳ Starting…') : '✨ Fill missing photos'}
          </button>

          {/* MASS STATUS CHANGE */}
          <div className="flex items-center gap-1 rounded border border-[#B49B7E] bg-black/30 px-2 py-1" data-testid="bulk-status-bar">
            <span className="text-xs font-semibold text-[#D4C5A9]">
              {selectedItemIds.size > 0 ? `Set ${selectedItemIds.size} selected to:` : `Set all shown (${gatherVisibleItemIds().length}) to:`}
            </span>
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              data-testid="bulk-status-select"
              className="bg-[#1a1a1a] text-white text-xs rounded px-2 py-1 border border-[#8B7355]"
            >
              <option value="">— status —</option>
              {['TO BE SELECTED','RESEARCHING','PENDING APPROVAL','APPROVED','ORDERED','PICKED','CONFIRMED','IN PRODUCTION','SHIPPED','IN TRANSIT','OUT FOR DELIVERY','DELIVERED TO RECEIVER','DELIVERED TO JOB SITE','RECEIVED','READY FOR INSTALL','INSTALLING','INSTALLED','ON HOLD','BACKORDERED','DAMAGED','RETURNED','CANCELLED'].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <button
              onClick={handleBulkStatus}
              disabled={bulkBusy || !bulkStatus}
              data-testid="bulk-status-apply-btn"
              className="text-xs font-bold rounded bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-2 py-1"
            >
              {bulkBusy ? '⏳…' : 'Apply'}
            </button>
            <button
              onClick={selectAllShown}
              data-testid="bulk-select-all-btn"
              title="Tick every item currently shown"
              className="text-xs font-semibold rounded border border-[#8B7355] text-[#D4C5A9] hover:bg-[#8B7355] hover:text-white px-2 py-1"
            >
              ☑ All
            </button>
            {selectedItemIds.size > 0 && (
              <button
                onClick={() => openMoveModal({ bulk: true })}
                data-testid="bulk-move-btn"
                title={`Move the ${selectedItemIds.size} selected item(s) to another room / category`}
                className="text-xs font-bold rounded bg-[#D4A574] hover:bg-[#E8D4B8] text-black px-2 py-1 flex items-center gap-1"
              >
                <FolderInput size={13} /> Move {selectedItemIds.size}
              </button>
            )}
            {selectedItemIds.size > 0 && (
              <button
                onClick={clearSelection}
                data-testid="bulk-clear-selection-btn"
                title="Clear the current selection"
                className="text-xs font-semibold rounded border border-[#8B7355] text-[#D4C5A9] hover:bg-[#8B7355] hover:text-white px-2 py-1"
              >
                ✕ Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ✅ SCRAPER CLIPBOARD NOTIFICATION - Shows when data is ready to paste */}
      {scraperClipboard && showScraperNotification && (
        <div className="mx-4 mb-4 p-4 rounded-lg border-2 border-green-500 animate-pulse" 
             style={{ 
               background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(22, 101, 52, 0.3) 100%)',
               boxShadow: '0 0 20px rgba(34, 197, 94, 0.3)'
             }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-3xl">📋</div>
              <div>
                <div className="text-green-400 font-bold text-lg">SCRAPED DATA READY TO PASTE!</div>
                <div className="text-white text-sm">
                  <span className="font-semibold">{scraperClipboard.name}</span>
                  {scraperClipboard.vendor && <span className="text-gray-300"> • {scraperClipboard.vendor}</span>}
                  {scraperClipboard.price && <span className="text-green-400"> • ${scraperClipboard.price}</span>}
                  {scraperClipboard.finish_color && <span className="text-amber-400"> • {scraperClipboard.finish_color}</span>}
                </div>
                <div className="text-gray-400 text-xs mt-1">👆 Click the <span className="text-green-400 font-bold">📋 PASTE</span> button on any item row to apply this data</div>
              </div>
            </div>
            <button 
              onClick={clearScraperClipboard}
              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
            >
              ✕ Clear
            </button>
          </div>
        </div>
      )}

      {/* ORIGINAL TABLE STRUCTURE - DO NOT CHANGE */}
      <div className="w-full overflow-x-auto" style={{ backgroundColor: '#0F172A', touchAction: 'pan-x' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', minWidth: '1200px' }}>
          
          <div className="w-full" style={{ touchAction: 'pan-x pan-y' }}>
            <table className="ffe-grid border-collapse border border-[#B49B7E]" style={{ width: 'max-content' }}>
                  
                  <thead>
                    {/* EMPTY HEADER FOR STRUCTURE */}
                  </thead>

                  {/* TABLE BODY WITH SMOOTH DRAG AND DROP */}
                  <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="ffe-rooms" type="room">
                    {(provided) => (
                      <tbody ref={provided.innerRef} {...provided.droppableProps}>
                {/* USE FILTERED PROJECT DATA */}
                {(() => {
                  const { floors, ordered } = buildOrderedRooms();
                  let lastFloor = null;
                  return ordered.map((room, roomIndex) => {
                  const currentFloor = room.floor || '1ST FLOOR';
                  const showFloorBanner = currentFloor !== lastFloor;
                  const floorIdx = floors.indexOf(currentFloor);
                  lastFloor = currentFloor;
                  const isFloorCollapsed = expandedFloors[currentFloor] === false;
                  const floorRoomCount = ordered.filter(r => (r.floor || '1ST FLOOR') === currentFloor).length;
                  const isRoomExpanded = !isFloorCollapsed && expandedRooms[room.id];
                  const roomColor = getColorByIndex(roomIndex); // Use index-based color to ensure no repeats
                  
                  return (
                    <React.Fragment key={room.id}>
                    {/* FLOOR BANNER - shown when floor changes */}
                    {showFloorBanner && (
                      <tr data-testid={`floor-banner-${currentFloor}`}>
                        <td colSpan="12" style={{
                          background: 'linear-gradient(90deg, #1a1a1a 0%, #2a2218 15%, #3d3020 50%, #2a2218 85%, #1a1a1a 100%)',
                          borderTop: '3px solid #D4A574', borderBottom: '3px solid #D4A574',
                          padding: '10px 16px',
                        }}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex flex-col gap-0" style={{ opacity: 0.6 }}>
                                {floorIdx > 0 && (
                                  <button onClick={() => moveFloor(floorIdx, floorIdx - 1)}
                                    className="text-[#D4A574] hover:text-white text-xs leading-none px-1" title="Move up">&#9650;</button>
                                )}
                                {floorIdx < floors.length - 1 && (
                                  <button onClick={() => moveFloor(floorIdx, floorIdx + 1)}
                                    className="text-[#D4A574] hover:text-white text-xs leading-none px-1" title="Move down">&#9660;</button>
                                )}
                              </div>
                              <button onClick={() => toggleFloor(currentFloor)} className="text-[#D4A574] text-lg w-6 text-center"
                                data-testid={`floor-toggle-${currentFloor}`}>
                                {isFloorCollapsed ? '▶' : '▼'}
                              </button>
                              <div style={{ width: '36px', height: '36px', borderRadius: '8px',
                                background: 'linear-gradient(135deg, #D4A574, #8B6914)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '16px', fontWeight: '900', color: '#000',
                                boxShadow: '0 2px 12px rgba(212, 165, 116, 0.4)',
                              }}>
                                {currentFloor.match(/\d+/) ? currentFloor.match(/\d+/)[0] : currentFloor.charAt(0)}
                              </div>
                              <span contentEditable={true} suppressContentEditableWarning={true}
                                className="outline-none px-1"
                                style={{ fontSize: '20px', fontWeight: '900', letterSpacing: '5px', color: '#D4A574',
                                  textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 0 20px rgba(212,165,116,0.3)',
                                }}
                                onBlur={(e) => {
                                  const n = e.target.textContent?.trim()?.toUpperCase();
                                  if (n && n !== currentFloor) renameFloor(currentFloor, n);
                                }}
                              >{currentFloor}</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <span style={{ color: '#D4A574', opacity: 0.5, fontSize: '11px', letterSpacing: '2px', fontWeight: '700' }}>
                                {floorRoomCount} ROOM{floorRoomCount !== 1 ? 'S' : ''}
                              </span>
                              {floors.length > 1 && (
                                <button onClick={() => { if (window.confirm(`Delete "${currentFloor}"? Rooms move to ${floors.find(f => f !== currentFloor)}.`)) deleteFloor(currentFloor); }}
                                  className="text-red-500/50 hover:text-red-400 text-sm px-2" title="Delete floor">&#10005;</button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    <Draggable draggableId={room.id} index={roomIndex}>
                      {(provided, snapshot) => (
                        <>
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style,
                              display: isFloorCollapsed ? 'none' : (snapshot.isDragging ? 'table' : ''),
                              backgroundColor: snapshot.isDragging ? 'rgba(212, 165, 116, 0.3)' : ''
                            }}
                          >
                            <td colSpan="12" 
                                className="border border-[#B49B7E] px-3 py-2 text-white text-sm font-bold"
                                style={{ 
                                  background: `linear-gradient(135deg, ${roomColor}FF 0%, ${roomColor}AA 20%, ${roomColor} 40%, ${roomColor}AA 80%, ${roomColor}FF 100%)`,
                                  boxShadow: `0 0 35px ${roomColor}80, inset 0 0 70px rgba(255, 255, 255, 0.16), inset 0 0 110px rgba(0, 0, 0, 0.5)`,
                                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 255, 255, 0.4)',
                                  position: 'relative',
                                  paddingRight: '104px'
                                }}>
                                <div className="flex items-center gap-2 w-full min-w-0">
                                  <div className="cursor-move text-white hover:text-gray-200 px-2 flex-shrink-0">
                                    ⋮⋮
                                  </div>
                                  <button
                                    onClick={() => toggleRoomExpansion(room.id)}
                                    className="text-white hover:text-gray-200 flex-shrink-0"
                                  >
                                    {isRoomExpanded ? '▼' : '▶'}
                                  </button>
                                  <span className="flex-1 min-w-[200px] text-white whitespace-normal break-words leading-tight" title={room.name}>{room.name.toUpperCase()}</span>
                                  <select
                                    data-testid={`floor-select-${room.id}`}
                                    className="ml-2 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider cursor-pointer flex-shrink-0 w-[118px] max-w-[118px]"
                                    style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid #D4A574', color: '#D4A574', outline: 'none' }}
                                    value={room.floor || '1ST FLOOR'}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={async (e) => {
                                      e.stopPropagation();
                                      let newFloor = e.target.value;
                                      if (newFloor === '__CUSTOM__') {
                                        const custom = window.prompt('Enter custom floor/section name:');
                                        if (!custom) return;
                                        newFloor = custom.toUpperCase();
                                        const current = getFloorOrder();
                                        if (!current.includes(newFloor)) await saveFloorOrder([...current, newFloor]);
                                      }
                                      try {
                                        const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);
                                        const resp = await fetch(`${backendUrl}/api/rooms/${room.id}`, {
                                          method: 'PUT',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ floor: newFloor })
                                        });
                                        if (!resp.ok) throw new Error(`Server returned ${resp.status}`);
                                        if (onReload) onReload();
                                      } catch (err) {
                                        console.error('Failed to update floor:', err);
                                        alert('Could not move the room to that floor — please try again.');
                                      }
                                    }}
                                  >
                                    {getFloorOrder().map(f => (
                                      <option key={f} value={f}>{f}</option>
                                    ))}
                                    <option value="__CUSTOM__">+ CUSTOM...</option>
                                  </select>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const baseUrl = window.location.origin;
                                      window.location.href = `${baseUrl}/project/${project.id}?tab=Room+Finishes&roomId=${room.id}`;
                                    }}
                                    className="ml-3 px-3 py-1 rounded-md text-[11px] font-black uppercase tracking-wider text-white flex-shrink-0"
                                    style={{
                                      background: 'linear-gradient(135deg, #D4A574 0%, #B8860B 50%, #D4A574 100%)',
                                      boxShadow: '0 0 16px rgba(212,165,116,0.7), 0 0 32px rgba(212,165,116,0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
                                      animation: 'glowPulse 2s ease-in-out infinite',
                                      textShadow: '0 1px 3px rgba(0,0,0,0.5)'
                                    }}
                                    data-testid={`finish-schedule-btn-${room.id}`}
                                  >
                                    FINISH SCHEDULE
                                  </button>
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const newLocked = !room.structure_locked;
                                      try {
                                        const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);
                                        await fetch(`${backendUrl}/api/rooms/${room.id}`, {
                                          method: 'PUT',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ structure_locked: newLocked })
                                        });
                                        toast.success(newLocked
                                          ? 'Structure locked — imports can’t change this room’s categories'
                                          : 'Structure unlocked');
                                        if (onReload) onReload();
                                      } catch (err) {
                                        console.error('Failed to toggle room lock:', err);
                                      }
                                    }}
                                    className="ml-2 px-2 py-1 rounded-md flex items-center gap-1 text-[10px] font-black uppercase tracking-wider flex-shrink-0 transition-colors"
                                    style={room.structure_locked
                                      ? { background: 'rgba(0,0,0,0.6)', border: '1px solid #F5C542', color: '#F5C542' }
                                      : { background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.45)', color: 'rgba(255,255,255,0.85)' }}
                                    title={room.structure_locked
                                      ? 'Structure LOCKED — imports won’t add or change this room’s categories. Click to unlock.'
                                      : 'Lock this room’s category structure so imports can’t change it'}
                                    data-testid={`room-lock-btn-${room.id}`}
                                  >
                                    {room.structure_locked ? <Lock size={12} /> : <Unlock size={12} />}
                                    {room.structure_locked ? 'LOCKED' : 'LOCK'}
                                  </button>
                                </div>
                                    <div className="flex items-center gap-3" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                                      <input
                                        type="checkbox"
                                        checked={selectedRoomsForDelete.has(room.id)}
                                        onChange={(e) => {
                                          const newSet = new Set(selectedRoomsForDelete);
                                          if (e.target.checked) newSet.add(room.id);
                                          else newSet.delete(room.id);
                                          setSelectedRoomsForDelete(newSet);
                                        }}
                                        className="w-5 h-5 cursor-pointer accent-red-500"
                                        title="Select room for bulk delete"
                                      />
                                      <button
                                        onClick={() => handleDeleteRoom(room.id)}
                                        className="text-red-300 hover:text-red-100 text-lg"
                                        title="Delete Room"
                                      >
                                        🗑️
                                      </button>
                                    </div>
                                  </td>
                                  <td className="border border-[#B49B7E] px-2 py-2 text-center rounded-lg text-[#D4C5A9]"
                                      style={{ 
                                        background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(30,30,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
                                        borderTop: `4px solid ${roomColor}`,
                                        boxShadow: `0 0 15px ${roomColor}40, inset 0 0 30px rgba(212, 165, 116, 0.04)`
                                      }}>
                                    <button
                                      onClick={() => handleAddRoom()}
                                      className="text-green-300 hover:text-green-100 text-sm font-bold"
                                      title="Add Room"
                                    >
                                      +
                                    </button>
                                  </td>
                                </tr>

                                {/* ROOM PHOTOS SECTION */}
                                {isRoomExpanded && roomPhotos[room.id]?.length > 0 && (
                                  <tr>
                                    <td colSpan="14" className="p-0">
                                      <div className="rounded-lg border border-[#D4A574]/30 overflow-hidden mx-2 my-2" 
                                           style={{ background: 'linear-gradient(135deg, rgba(20,20,30,0.95) 0%, rgba(30,30,40,0.9) 100%)' }}>
                                        <div 
                                          className="px-4 py-2 cursor-pointer flex items-center justify-between"
                                          style={{ 
                                            background: 'linear-gradient(135deg, rgba(212, 165, 116, 0.2) 0%, rgba(180, 155, 126, 0.15) 100%)',
                                            borderBottom: expandedPhotoRooms[room.id] ? '1px solid rgba(212, 165, 116, 0.3)' : 'none'
                                          }}
                                          onClick={() => togglePhotoFolder(room.id)}
                                        >
                                          <div className="flex items-center gap-3">
                                            <span className="text-xl">📁</span>
                                            <span className="text-[#D4A574] font-semibold text-sm">
                                              ROOM PHOTOS ({roomPhotos[room.id]?.length || 0})
                                            </span>
                                          </div>
                                          <span className="text-[#B49B7E]">
                                            {expandedPhotoRooms[room.id] ? '▼' : '▶'}
                                          </span>
                                        </div>
                                        
                                        {expandedPhotoRooms[room.id] && (
                                          <div className="p-4">
                                            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                                              {roomPhotos[room.id]?.map((photo, idx) => (
                                                <div 
                                                  key={photo.id || idx} 
                                                  className="relative group cursor-pointer rounded-lg overflow-hidden border border-[#B49B7E]/30 hover:border-[#D4A574] transition-all hover:scale-105"
                                                  style={{ aspectRatio: '1/1' }}
                                                  onClick={() => setSelectedPhotoView(photo)}
                                                >
                                                  <img 
                                                    src={photo.photo_data || photo.url || photo.image_url} 
                                                    alt={photo.file_name || `Photo ${idx + 1}`}
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                  />
                                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                    <span className="text-white text-2xl">🔍</span>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}

                                {/* ROOM CATEGORIES WITH SMOOTH DRAG DROP */}
                                {isRoomExpanded && (
                                  <Droppable droppableId={`categories-${room.id}`} type="category">
                                    {(provided) => (
                                      <>
                                        <tr ref={provided.innerRef} {...provided.droppableProps} style={{ display: 'none' }}>
                                          <td></td>
                                        </tr>
                                        {room.categories?.map((category, catIndex) => {
                                          const isCategoryExpanded = expandedCategories[category.id];
                                          console.log(`📁 RENDERING CATEGORY ${catIndex}: ${category.name} with ${category.subcategories?.length || 0} subcategories`);
                                          
                                          return (
                                            <Draggable key={category.id} draggableId={category.id} index={catIndex}>
                                              {(provided, snapshot) => (
                                                <>
                                                  {/* CATEGORY HEADER ROW */}
                                                  <tr
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                      ...provided.draggableProps.style,
                                                      display: snapshot.isDragging ? 'table' : '',
                                                      backgroundColor: snapshot.isDragging ? 'rgba(212, 165, 116, 0.3)' : ''
                                                    }}
                                                  >
                                                    <td colSpan="14" 
                                                        className="border border-[#B49B7E] px-4 py-2 text-white text-sm font-bold"
                                                        style={{ 
                                                          background: `linear-gradient(135deg, ${getCategoryColor()} 0%, ${getCategoryColor()}DD 50%, ${getCategoryColor()} 100%)`,
                                                          boxShadow: `0 0 22px ${getCategoryColor()}55, inset 0 0 45px rgba(255, 255, 255, 0.12), inset 0 0 85px rgba(0, 0, 0, 0.4)`,
                                                          textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 14px rgba(255, 255, 255, 0.3)'
                                                        }}>
                                                      <div className="flex items-center gap-2">
                                                        <div className="cursor-move text-white hover:text-gray-200 px-1">
                                                          ⋮⋮
                                                        </div>
                                                        <button
                                                          onClick={() => toggleCategoryExpansion(category.id)}
                                                          className="text-white hover:text-gray-200"
                                                        >
                                                          {isCategoryExpanded ? '▼' : '▶'}
                                                        </button>
                                                        <span>{category.name.toUpperCase()}</span>
                                                      </div>
                                                    </td>
                                                  </tr>

                                                  {/* SUBCATEGORIES - Only show when category expanded */}
                                                  {isCategoryExpanded && (
                                                    <React.Fragment>
                                                      {/* SECTION HEADER - GROUPS COLUMNS */}
                                                      <tr>
                                                        <td colSpan="4" className="border-gray-400 px-2 py-1 text-xs font-bold text-white text-center" 
                                                            style={{ backgroundColor: '#8B4444', borderLeft: '1px solid #9CA3AF', borderRight: 'none', borderTop: '1px solid #9CA3AF', borderBottom: '1px solid #9CA3AF' }}>
                                                        </td>
                                                        <td colSpan="3" className="border border-[#B49B7E] px-2 py-1 text-xs font-bold text-white text-center rounded" 
                                                            style={{ 
                                                              background: 'linear-gradient(135deg, #8B4513FF 0%, #8B4513AA 20%, #8B4513 40%, #8B4513AA 80%, #8B4513FF 100%)',
                                                              boxShadow: '0 0 25px #8B451360, inset 0 0 50px rgba(255, 255, 255, 0.14), inset 0 0 80px rgba(0, 0, 0, 0.45)',
                                                              textShadow: '0 2px 6px rgba(0, 0, 0, 0.75), 0 0 16px rgba(255, 255, 255, 0.35)'
                                                            }}>
                                                          ADDITIONAL INFO.
                                                        </td>
                                                        <td colSpan="2" className="border border-[#D4A574] px-2 py-1 text-xs font-bold text-white text-center rounded" 
                                                            style={{ 
                                                              background: 'linear-gradient(135deg, #D4A574FF 0%, #D4A574AA 20%, #D4A574 40%, #D4A574AA 80%, #D4A574FF 100%)',
                                                              boxShadow: '0 0 25px #D4A57460, inset 0 0 50px rgba(255, 255, 255, 0.14), inset 0 0 80px rgba(0, 0, 0, 0.45)',
                                                              textShadow: '0 2px 6px rgba(0, 0, 0, 0.75), 0 0 16px rgba(255, 255, 255, 0.35)'
                                                            }}>
                                                          STOCK INFO.
                                                        </td>
                                                        <td colSpan="6" className="border border-[#B49B7E] px-2 py-1 text-xs font-bold text-white text-center rounded" 
                                                            style={{ 
                                                              background: 'linear-gradient(135deg, #6B46C1FF 0%, #6B46C1AA 20%, #6B46C1 40%, #6B46C1AA 80%, #6B46C1FF 100%)',
                                                              boxShadow: '0 0 25px #6B46C160, inset 0 0 50px rgba(255, 255, 255, 0.14), inset 0 0 80px rgba(0, 0, 0, 0.45)',
                                                              textShadow: '0 2px 6px rgba(0, 0, 0, 0.75), 0 0 16px rgba(255, 255, 255, 0.35)'
                                                            }}>
                                                          SHIPPING INFO.
                                                        </td>
                                                        <td colSpan="2" className="border-gray-400 px-2 py-1 text-xs font-bold text-white text-center" 
                                                            style={{ backgroundColor: '#8B4444', borderRight: '1px solid #9CA3AF', borderLeft: 'none', borderTop: '1px solid #9CA3AF', borderBottom: '1px solid #9CA3AF' }}>
                                                        </td>
                                                      </tr>
                                                      
                                                      {/* MAIN RED HEADER ROW - PERFECTLY ALIGNED WITH DATA COLUMNS */}
                                                      <tr>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #8B4444FF 0%, #8B4444AA 20%, #8B4444 40%, #8B4444AA 80%, #8B4444FF 100%)',
                                    boxShadow: '0 0 20px #8B444450, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>INSTALLED</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #8B4444FF 0%, #8B4444AA 20%, #8B4444 40%, #8B4444AA 80%, #8B4444FF 100%)',
                                    boxShadow: '0 0 20px #8B444450, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>VENDOR/SKU</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #8B4444FF 0%, #8B4444AA 20%, #8B4444 40%, #8B4444AA 80%, #8B4444FF 100%)',
                                    boxShadow: '0 0 20px #8B444450, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>QTY</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #8B4444FF 0%, #8B4444AA 20%, #8B4444 40%, #8B4444AA 80%, #8B4444FF 100%)',
                                    boxShadow: '0 0 20px #8B444450, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>SIZE</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #8B4513FF 0%, #8B4513AA 20%, #8B4513 40%, #8B4513AA 80%, #8B4513FF 100%)',
                                    boxShadow: '0 0 20px #8B451350, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>FINISH/COLOR</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #8B4513FF 0%, #8B4513AA 20%, #8B4513 40%, #8B4513AA 80%, #8B4513FF 100%)',
                                    boxShadow: '0 0 20px #8B451350, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>COST/PRICE</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #8B4513FF 0%, #8B4513AA 20%, #8B4513 40%, #8B4513AA 80%, #8B4513FF 100%)',
                                    boxShadow: '0 0 20px #8B451350, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>IMAGE</td>
                                                        <td className="border border-[#D4A574] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #D4A574FF 0%, #D4A574AA 20%, #D4A574 40%, #D4A574AA 80%, #D4A574FF 100%)',
                                    boxShadow: '0 0 20px #D4A57450, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>STOCK STATUS/QTY</td>
                                                        <td className="border border-[#D4A574] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #D4A574FF 0%, #D4A574AA 20%, #D4A574 40%, #D4A574AA 80%, #D4A574FF 100%)',
                                    boxShadow: '0 0 20px #D4A57450, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>RESTOCK/LEAD TIME</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #6B46C1FF 0%, #6B46C1AA 20%, #6B46C1 40%, #6B46C1AA 80%, #6B46C1FF 100%)',
                                    boxShadow: '0 0 20px #6B46C150, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>ORDER DATE</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #6B46C1FF 0%, #6B46C1AA 20%, #6B46C1 40%, #6B46C1AA 80%, #6B46C1FF 100%)',
                                    boxShadow: '0 0 20px #6B46C150, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>STATUS/ORDER#</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #6B46C1FF 0%, #6B46C1AA 20%, #6B46C1 40%, #6B46C1AA 80%, #6B46C1FF 100%)',
                                    boxShadow: '0 0 20px #6B46C150, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>EST. DATES</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #6B46C1FF 0%, #6B46C1AA 20%, #6B46C1 40%, #6B46C1AA 80%, #6B46C1FF 100%)',
                                    boxShadow: '0 0 20px #6B46C150, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>INSTALL/SHIP TO</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #6B46C1FF 0%, #6B46C1AA 20%, #6B46C1 40%, #6B46C1AA 80%, #6B46C1FF 100%)',
                                    boxShadow: '0 0 20px #6B46C150, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>TRACKING/CARRIER</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #6B46C1FF 0%, #6B46C1AA 20%, #6B46C1 40%, #6B46C1AA 80%, #6B46C1FF 100%)',
                                    boxShadow: '0 0 20px #6B46C150, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>NOTES</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #6B46C1FF 0%, #6B46C1AA 20%, #6B46C1 40%, #6B46C1AA 80%, #6B46C1FF 100%)',
                                    boxShadow: '0 0 20px #6B46C150, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>REMARKS</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #059669FF 0%, #059669AA 20%, #059669 40%, #059669AA 80%, #059669FF 100%)',
                                    boxShadow: '0 0 20px #05966950, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>INSTALL NOTES</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #8B4444FF 0%, #8B4444AA 20%, #8B4444 40%, #8B4444AA 80%, #8B4444FF 100%)',
                                    boxShadow: '0 0 20px #8B444450, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>LINK</td>
                                                        <td className="border border-[#B49B7E] px-3 py-2 text-xs font-bold text-white" style={{ 
                                    background: 'linear-gradient(135deg, #8B4444FF 0%, #8B4444AA 20%, #8B4444 40%, #8B4444AA 80%, #8B4444FF 100%)',
                                    boxShadow: '0 0 20px #8B444450, inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7), 0 0 12px rgba(255, 255, 255, 0.3)'
                                  }}>DELETE</td>
                                                      </tr>
                                                      
                                                      {/* INSTALLEDS GO DIRECTLY UNDER RED HEADER */}
                                                      {/* ACTUAL INSTALLEDS FROM BACKEND DATA - SORTED with linked items at TOP */}
                                                      <Droppable droppableId={`items-${category.id}`} type="item">
                                                      {(itemsDropProvided) => (
                                                      <>
                                                      <tr ref={itemsDropProvided.innerRef} {...itemsDropProvided.droppableProps} style={{ display: 'none' }}><td /></tr>
                                                      {(() => {
                                                        // Collect all items from all subcategories
                                                        const allItems = [];
                                                        category.subcategories?.forEach((subcategory) => {
                                                          (subcategory.items || []).forEach(item => {
                                                            allItems.push({ ...item, subcategoryId: subcategory.id, subcategoryName: subcategory.name });
                                                          });
                                                        });
                                                        
                                                        // Sort: Items on INCOMPLETE To-Do or Punch List FIRST
                                                        const sortedItems = allItems.sort((a, b) => {
                                                          const aTodoInfo = todoLinkedItems.get(a.id);
                                                          const aPunchInfo = linkedPunchItems[a.id];
                                                          const aOnIncompleteList = (aTodoInfo && !aTodoInfo.completed) || (aPunchInfo && !aPunchInfo.is_completed);
                                                          
                                                          const bTodoInfo = todoLinkedItems.get(b.id);
                                                          const bPunchInfo = linkedPunchItems[b.id];
                                                          const bOnIncompleteList = (bTodoInfo && !bTodoInfo.completed) || (bPunchInfo && !bPunchInfo.is_completed);
                                                          
                                                          if (aOnIncompleteList && !bOnIncompleteList) return -1;
                                                          if (!aOnIncompleteList && bOnIncompleteList) return 1;
                                                          return 0;
                                                        });
                                                        
                                                        // Counter for alternating highlighter colors
                                                        let highlightIndex = 0;
                                                        
                                                        // BRIGHT alternating highlighter colors - ONLY for items on To-Do or Punch List
                                                        const highlighterColors = [
                                                          '#FFFF00',  // Bright Yellow
                                                          '#00FF7F',  // Bright Spring Green
                                                          '#FF69B4',  // Hot Pink
                                                          '#00BFFF',  // Deep Sky Blue
                                                          '#FFA500',  // Bright Orange
                                                          '#DA70D6',  // Orchid/Plum
                                                        ];
                                                        
                                                        // Regroup items by subcategory order (stable sort keeps To-Do/Punch priority within each sub)
                                                        const subOrder = new Map((category.subcategories || []).map((s, i) => [s.id, i]));
                                                        const subCounts = {};
                                                        sortedItems.forEach(it => { subCounts[it.subcategoryId] = (subCounts[it.subcategoryId] || 0) + 1; });
                                                        sortedItems.sort((a, b) => (subOrder.get(a.subcategoryId) ?? 999) - (subOrder.get(b.subcategoryId) ?? 999));
                                                        let __lastSubId = null;
                                                        sortedItems.forEach(it => { it.__isFirstOfSub = it.subcategoryId !== __lastSubId; it.__subCount = subCounts[it.subcategoryId] || 0; __lastSubId = it.subcategoryId; });

                                                        return sortedItems.map((item, itemIndex) => {
                                                          // Check if item is linked to punch list or to-do
                                                          const punchLink = linkedPunchItems[item.id];
                                                          const todoInfo = todoLinkedItems.get(item.id);
                                                          const isOnPunchList = !!punchLink;
                                                          const isOnTodo = !!todoInfo;
                                                          const punchCompleted = punchLink?.is_completed;
                                                          const todoCompleted = todoInfo?.completed;
                                                          
                                                          // Only highlight if on list AND the linked item is NOT complete
                                                          const shouldHighlight = (isOnPunchList && !punchCompleted) || (isOnTodo && !todoCompleted);
                                                          
                                                          // Determine row style
                                                          let rowStyle;
                                                          // Text color: BLACK for highlighted rows, normal for others
                                                          const textColor = shouldHighlight ? '#000' : '#B49B7E';
                                                          
                                                          if (shouldHighlight) {
                                                            rowStyle = { background: highlighterColors[highlightIndex % highlighterColors.length] };
                                                            highlightIndex++;
                                                          } else {
                                                            rowStyle = { 
                                                              background: itemIndex % 2 === 0 
                                                                ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(30, 30, 30, 0.9) 30%, rgba(15, 15, 25, 0.95) 70%, rgba(0, 0, 0, 0.95) 100%)'
                                                                : 'linear-gradient(135deg, rgba(15, 15, 25, 0.95) 0%, rgba(45, 45, 55, 0.9) 30%, rgba(25, 25, 35, 0.95) 70%, rgba(15, 15, 25, 0.95) 100%)'
                                                            };
                                                          }
                                                          
                                                          return (
                                                        <React.Fragment key={item.id}>
                                                          {item.__isFirstOfSub && (
                                                            <tr onDragOver={dragOverIfItem} onDrop={(e) => onItemDropToSub(e, item.subcategoryId)}>
                                                              <td colSpan="14" className="border border-[#B49B7E] px-4 py-1.5 text-white text-xs font-bold"
                                                                  style={{ background: 'linear-gradient(135deg, #6B4A9E 0%, #6B4A9EDD 50%, #6B4A9E 100%)', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                                                                <div className="flex items-center gap-2 pl-6">
                                                                  <span className="opacity-80">↳</span>
                                                                  <span>{(item.subcategoryName || 'ITEMS').toUpperCase()}</span>
                                                                  <span className="opacity-70 font-normal">({item.__subCount})</span>
                                                                </div>
                                                              </td>
                                                            </tr>
                                                          )}
                                                        <tr key={item.id}
                                                          onDragOver={dragOverIfItem}
                                                          onDrop={(e) => onItemDropToSub(e, item.subcategoryId)}
                                                          style={rowStyle}
                                                        >
                                                          {/* INSTALLED - INSTALLED NAME GOES HERE */}
                                                          <td className="border border-[#B49B7E] px-2 py-2 text-sm relative" style={{ color: textColor }}>
                                                            <div className="flex items-center gap-2">
                                                              <span
                                                                draggable
                                                                onDragStart={(e) => onItemDragStart(e, item)}
                                                                className="cursor-grab active:cursor-grabbing text-[#D4A574] hover:text-white select-none flex-shrink-0 text-sm leading-none"
                                                                title="Drag to move this item to another section"
                                                                data-testid={`item-drag-handle-${item.id}`}
                                                              >⋮⋮</span>
                                                              <button
                                                                onClick={(e) => { e.stopPropagation(); openMoveModal(item); }}
                                                                className="text-[#D4A574] hover:text-white hover:bg-white/10 rounded p-0.5 flex-shrink-0 transition-colors"
                                                                title="Move this item to another room / category"
                                                                data-testid={`item-move-btn-${item.id}`}
                                                              ><FolderInput size={14} /></button>
                                                              <input
                                                                type="checkbox"
                                                                checked={selectedItemIds.has(item.id)}
                                                                onChange={() => toggleItemSelect(item.id)}
                                                                data-testid={`item-select-${item.id}`}
                                                                title="Select this item for a bulk status change or bulk move"
                                                                className="h-4 w-4 flex-shrink-0 cursor-pointer accent-amber-600"
                                                              />
                                                              {shouldHighlight && (
                                                                <span className="text-xs font-bold text-black">
                                                                  {isOnTodo && '📋'}
                                                                  {isOnPunchList && '🔨'}
                                                                </span>
                                                              )}
                                                              {isOnPunchList && (
                                                                <span 
                                                                  className={`text-xs px-1.5 py-0.5 rounded ${punchCompleted ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}
                                                                  title={`Punch List: ${punchLink.punch_title} (${punchLink.punch_status})`}
                                                                >
                                                                  {punchCompleted ? '✓ DONE' : 'PUNCH'}
                                                                </span>
                                                              )}
                                                              {isOnTodo && !isOnPunchList && (
                                                                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-600 text-white">
                                                                  TO-DO
                                                                </span>
                                                              )}
                                                              <span
                                                                title={item.name || ''}
                                                                className={`${shouldHighlight ? 'font-bold ' : ''}${(item.name || '').length > 60 ? 'text-[10px] leading-tight' : (item.name || '').length > 38 ? 'text-xs leading-snug' : 'text-sm'} min-w-0`}
                                                                style={{
                                                                  display: 'inline-block',
                                                                  maxWidth: '240px',
                                                                  whiteSpace: 'normal',
                                                                  overflowWrap: 'anywhere',
                                                                  wordBreak: (item.name || '').length > 38 ? 'break-all' : 'break-word',
                                                                }}
                                                              >{item.name}</span>
                                                            </div>
                                                            {(isPaintRow(category.name, item.subcategoryName) || isCabinetRow(category.name, item.subcategoryName)) && (
                                                              <div
                                                                className="mt-2 ml-6 flex flex-wrap items-center gap-1.5"
                                                                data-testid={`paint-controls-${item.id}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                              >
                                                                {isPaintProper(item.subcategoryName) ? (
                                                                  PAINT_AREAS.map((area) => {
                                                                    const sheens = (item.area_sheens && typeof item.area_sheens === 'object' && !Array.isArray(item.area_sheens)) ? item.area_sheens : {};
                                                                    const active = Object.prototype.hasOwnProperty.call(sheens, area);
                                                                    return (
                                                                      <span key={area} className="inline-flex items-center gap-0.5">
                                                                        <button
                                                                          type="button"
                                                                          onClick={() => {
                                                                            const cur = { ...sheens };
                                                                            if (active) { delete cur[area]; } else { cur[area] = ''; }
                                                                            handleUpdateItemField(item.id, 'area_sheens', cur);
                                                                          }}
                                                                          data-testid={`item-area-${area.toLowerCase()}-${item.id}`}
                                                                          title={`Paint placement: ${area}`}
                                                                          className={`text-[11px] leading-none px-2 py-1 rounded border transition-colors ${active ? 'bg-[#7a5a1f] text-[#FFF3DD] border-[#D4A574] font-bold' : 'bg-transparent text-[#D8C3A5] border-[#B49B7E]/60 hover:border-[#D4A574] hover:text-white'}`}
                                                                        >{area}</button>
                                                                        {active && (
                                                                          <select
                                                                            value={sheens[area] || ''}
                                                                            onChange={(e) => { const cur = { ...sheens }; cur[area] = e.target.value; handleUpdateItemField(item.id, 'area_sheens', cur); }}
                                                                            data-testid={`item-areasheen-${area.toLowerCase()}-${item.id}`}
                                                                            title={`${area} sheen`}
                                                                            className="bg-[#2a2a2a] border border-[#D4A574]/70 text-[#E8D9C4] text-[11px] rounded px-1.5 py-1 outline-none cursor-pointer"
                                                                          >
                                                                            <option value="" style={{ background: '#2a2a2a', color: '#E8D9C4' }}>sheen…</option>
                                                                            {PAINT_SHEENS.map((s) => <option key={s} value={s} style={{ background: '#2a2a2a', color: '#E8D9C4' }}>{s}</option>)}
                                                                          </select>
                                                                        )}
                                                                      </span>
                                                                    );
                                                                  })
                                                                ) : (
                                                                  <>
                                                                    <select
                                                                      value={item.sheen || ''}
                                                                      onChange={(e) => handleUpdateItemField(item.id, 'sheen', e.target.value)}
                                                                      data-testid={`item-sheen-${item.id}`}
                                                                      title="Sheen"
                                                                      className="bg-[#2a2a2a] border border-[#B49B7E]/60 text-[#E8D9C4] text-[11px] rounded px-1.5 py-1 outline-none cursor-pointer"
                                                                    >
                                                                      <option value="" style={{ background: '#2a2a2a', color: '#E8D9C4' }}>Sheen…</option>
                                                                      {PAINT_SHEENS.map((s) => <option key={s} value={s} style={{ background: '#2a2a2a', color: '#E8D9C4' }}>{s}</option>)}
                                                                    </select>
                                                                    {showsPaintAreas(item.subcategoryName) && PAINT_AREAS.map((area) => {
                                                                      const active = Array.isArray(item.paint_areas) && item.paint_areas.includes(area);
                                                                      return (
                                                                        <button
                                                                          key={area}
                                                                          type="button"
                                                                          onClick={() => {
                                                                            const cur = Array.isArray(item.paint_areas) ? item.paint_areas : [];
                                                                            const next = cur.includes(area) ? cur.filter((a) => a !== area) : [...cur, area];
                                                                            handleUpdateItemField(item.id, 'paint_areas', next);
                                                                          }}
                                                                          data-testid={`item-area-${area.toLowerCase()}-${item.id}`}
                                                                          title={`Placement: ${area}`}
                                                                          className={`text-[11px] leading-none px-2 py-1 rounded border transition-colors ${active ? 'bg-[#7a5a1f] text-[#FFF3DD] border-[#D4A574] font-bold' : 'bg-transparent text-[#D8C3A5] border-[#B49B7E]/60 hover:border-[#D4A574] hover:text-white'}`}
                                                                        >{area}</button>
                                                                      );
                                                                    })}
                                                                  </>
                                                                )}
                                                                {(isCabinetRow(category.name, item.subcategoryName) ? CABINET_SPEC_CHECKS : PAINT_SPEC_CHECKS).map((spec) => {
                                                                  const active = Array.isArray(item.spec_checks) && item.spec_checks.includes(spec);
                                                                  return (
                                                                    <button
                                                                      key={spec}
                                                                      type="button"
                                                                      onClick={() => {
                                                                        const cur = Array.isArray(item.spec_checks) ? item.spec_checks : [];
                                                                        const next = cur.includes(spec) ? cur.filter((a) => a !== spec) : [...cur, spec];
                                                                        handleUpdateItemField(item.id, 'spec_checks', next);
                                                                      }}
                                                                      data-testid={`item-spec-${spec.toLowerCase().replace(/\s+/g, '-')}-${item.id}`}
                                                                      title={`Spec confirmed: ${spec}`}
                                                                      className={`text-[11px] leading-none px-2 py-1 rounded border transition-colors flex items-center ${active ? 'bg-emerald-600 text-white border-emerald-400 font-bold' : 'bg-transparent text-[#aec8ae] border-[#6f8a6f]/70 hover:border-emerald-400 hover:text-white'}`}
                                                                    >{active ? '✓ ' : ''}{spec}</button>
                                                                  );
                                                                })}
                                                              </div>
                                                            )}
                                                          </td>
                                                          
                                                          {/* VENDOR/SKU - EDITABLE INLINE */}
                                                          <td className="border border-[#B49B7E] px-2 py-2 text-sm text-[#B49B7E]">
                                                            <div 
                                                              contentEditable={true}
                                                              suppressContentEditableWarning={true}
                                                              className="w-full bg-transparent text-white text-sm outline-none"
                                                              onBlur={(e) => {
                                                                const newValue = e.target.textContent?.trim();
                                                                if (newValue !== item.vendor) {
                                                                  handleUpdateItemField(item.id, 'vendor', newValue);
                                                                }
                                                              }}
                                                            >
                                                              {item.vendor || ''}
                                                            </div>
                                                          </td>
                                                          
                                                          {/* QTY - EDITABLE INLINE */}
                                                          <td className="border border-[#B49B7E] px-2 py-2 text-sm text-center text-white">
                                                            <div 
                                                              contentEditable={true}
                                                              suppressContentEditableWarning={true}
                                                              className="w-full bg-transparent text-white text-sm text-center outline-none"
                                                              onBlur={(e) => {
                                                                const newValue = e.target.textContent?.trim();
                                                                const numValue = parseInt(newValue);
                                                                if (!isNaN(numValue) && numValue !== item.quantity) {
                                                                  handleUpdateItemField(item.id, 'quantity', numValue);
                                                                }
                                                              }}
                                                            >
                                                              {item.quantity || ''}
                                                            </div>
                                                          </td>
                                                          
                                                          {/* SIZE - EDITABLE INLINE */}
                                                          <td className="border border-[#B49B7E] px-2 py-2 text-sm text-[#B49B7E]" style={{ minWidth: '110px' }}>
                                                            <div 
                                                              contentEditable={true}
                                                              suppressContentEditableWarning={true}
                                                              className="w-full bg-transparent text-white text-sm outline-none"
                                                              style={{ whiteSpace: 'normal', wordBreak: 'normal', overflowWrap: 'break-word' }}
                                                              onBlur={(e) => {
                                                                const newValue = e.target.textContent?.trim();
                                                                if (newValue !== item.size) {
                                                                  handleUpdateItemField(item.id, 'size', newValue);
                                                                }
                                                              }}
                                                            >
                                                              {item.size || ''}
                                                            </div>
                                                          </td>
                                                          
                                                          {/* FINISH/Color with Swatch Image - EDITABLE INLINE */}
                                                          <td className="border border-[#B49B7E] px-2 py-2 text-sm text-[#B49B7E]">
                                                            <div className="flex items-center gap-2">
                                                              {/* Finish Swatch Image */}
                                                              {item.finish_image && (
                                                                <img 
                                                                  src={item.finish_image} 
                                                                  alt={item.finish_color || 'Finish'} 
                                                                  className="w-8 h-8 object-cover rounded border border-[#B49B7E] cursor-pointer hover:scale-110 transition-transform" 
                                                                  onClick={() => openLightbox(item, item.finish_image)}
                                                                  title={`Click to view ${item.finish_color || 'finish'} swatch`}
                                                                />
                                                              )}
                                                              <div 
                                                                contentEditable={true}
                                                                suppressContentEditableWarning={true}
                                                                className="flex-1 bg-transparent text-white text-sm outline-none"
                                                                onBlur={(e) => {
                                                                  const newValue = e.target.textContent?.trim();
                                                                  if (newValue !== item.finish_color) {
                                                                    handleUpdateItemField(item.id, 'finish_color', newValue);
                                                                  }
                                                                }}
                                                              >
                                                                {item.finish_color || ''}
                                                              </div>
                                                              <label
                                                                onClick={(e) => e.stopPropagation()}
                                                                data-testid={`finish-upload-${item.id}`}
                                                                title={item.finish_image ? 'Replace finish swatch by hand' : 'Add a finish/color swatch by hand'}
                                                                className="text-[10px] shrink-0 text-[#B49B7E] hover:text-white cursor-pointer border border-dashed border-[#B49B7E]/50 rounded px-1 leading-tight"
                                                              >
                                                                {item.finish_image ? '↻' : '＋'}
                                                                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleFinishUpload(item.id, f); }} />
                                                              </label>
                                                            </div>
                                                          </td>
                                                          
                                                          {/* Cost/Price - EDITABLE INLINE */}
                                                          <td className="border border-[#B49B7E] px-2 py-2 text-sm text-[#B49B7E]">
                                                            <div 
                                                              contentEditable={true}
                                                              suppressContentEditableWarning={true}
                                                              className="w-full bg-transparent text-white text-sm outline-none"
                                                              onBlur={(e) => {
                                                                const newValue = e.target.textContent?.replace(/[$,]/g, '').trim();
                                                                const numValue = parseFloat(newValue);
                                                                if (!isNaN(numValue) && numValue !== item.cost) {
                                                                  handleUpdateItemField(item.id, 'cost', numValue);
                                                                }
                                                              }}
                                                            >
                                                              {item.cost ? `$${item.cost}` : ''}
                                                            </div>
                                                          </td>
                                                          
                                                          {/* Image - SCRAPED AUTOMATICALLY - CLICKABLE TO EXPAND */}
                                                          <td className="border border-[#B49B7E] px-2 py-2 text-center text-white">
                                                            {item.image_url ? (
                                                              <img 
                                                                src={item.image_url} 
                                                                alt={item.name} 
                                                                className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity" 
                                                                onClick={() => openLightbox(item, item.image_url)}
                                                                title="Click to expand"
                                                              />
                                                            ) : (
                                                              <div className="w-16 h-16 bg-gray-700 rounded flex items-center justify-center text-xs">No Image</div>
                                                            )}
                                                            <button
                                                              onClick={() => setPhotoItem(item)}
                                                              data-testid={`item-photos-btn-${item.id}`}
                                                              title="Photos for this item"
                                                              className="mt-1 w-16 text-[10px] font-bold rounded border border-[#D4A574]/60 text-[#D4A574] hover:bg-[#D4A574] hover:text-black transition-colors py-0.5"
                                                            >
                                                              {(item.photos || []).length > 0
                                                                ? `📷 ${(item.photos || []).length}`
                                                                : '📷 Add'}
                                                            </button>
                                                            <button
                                                              onClick={() => handleRefreshItemPhoto(item)}
                                                              disabled={refreshingItemId === item.id}
                                                              data-testid={`item-hires-btn-${item.id}`}
                                                              title="Find a product photo (from the vendor link, or by name/SKU) and attach it"
                                                              className="mt-1 w-16 text-[10px] font-bold rounded border border-[#8B7355]/70 text-[#D4C5A9] hover:bg-[#8B7355] hover:text-white transition-colors py-0.5 disabled:opacity-50"
                                                            >
                                                              {refreshingItemId === item.id ? '⏳…' : '🔎 Find photo'}
                                                            </button>
                                                            <button
                                                              onClick={() => handleDescribeItem(item)}
                                                              disabled={describingItemId === item.id}
                                                              data-testid={`item-describe-btn-${item.id}`}
                                                              title="Generate a client-ready description + specs with AI"
                                                              className="mt-1 w-16 text-[10px] font-bold rounded border border-[#D4A574]/60 text-[#D4A574] hover:bg-[#D4A574] hover:text-black transition-colors py-0.5 disabled:opacity-50"
                                                            >
                                                              {describingItemId === item.id ? '⏳…' : '✨ Describe'}
                                                            </button>
                                                          </td>
                                                          
                                                          {/* STOCK STATUS/QTY - STACKED - COLORED BY STOCK STATUS */}
                                                          <td className="border border-[#D4A574] px-1 py-1 text-sm" style={{
                                                            background: item.stock_status ? `linear-gradient(135deg, ${getStockStatusColor(item.stock_status)}FF 0%, ${getStockStatusColor(item.stock_status)}AA 20%, ${getStockStatusColor(item.stock_status)} 40%, ${getStockStatusColor(item.stock_status)}AA 80%, ${getStockStatusColor(item.stock_status)}FF 100%)` : 'transparent',
                                                            boxShadow: item.stock_status ? `0 0 15px ${getStockStatusColor(item.stock_status)}40, inset 0 0 30px rgba(255, 255, 255, 0.1), inset 0 0 50px rgba(0, 0, 0, 0.3)` : 'none'
                                                          }}>
                                                            <div className="flex flex-col h-full">
                                                              <div className="h-6 mb-1">
                                                                <select 
                                                                  value={item.stock_status || ''}
                                                                  className="w-full h-full bg-transparent border-none text-white text-xs p-0"
                                                                  onChange={async (e) => {
                                                                    try {
                                                                      const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
                                                                      const response = await fetch(`${backendUrl}/api/items/${item.id}`, {
                                                                        method: 'PUT',
                                                                        headers: { 'Content-Type': 'application/json' },
                                                                        body: JSON.stringify({ stock_status: e.target.value })
                                                                      });
                                                                      
                                                                      if (response.ok) {
                                                                        console.log('✅ Stock status saved:', e.target.value);
                                                                        // Update local state and reload
                                                                        item.stock_status = e.target.value;
                                                                        if (onReload) {
                                                                          onReload();
                                                                        }
                                                                      } else {
                                                                        console.error('❌ Failed to save stock status');
                                                                        alert('Failed to save stock status');
                                                                      }
                                                                    } catch (error) {
                                                                      console.error('❌ Error saving stock status:', error);
                                                                      alert('Error saving stock status');
                                                                    }
                                                                  }}
                                                                >
                                                                  <option value="">—</option>
                                                                  <option value="IN STOCK">✅ IN STOCK</option>
                                                                  <option value="LOW STOCK">⚠️ LOW STOCK</option>
                                                                  <option value="OUT OF STOCK">❌ OUT OF STOCK</option>
                                                                  <option value="BACKORDERED">⏳ BACKORDERED</option>
                                                                  <option value="DISCONTINUED">🚫 DISCONTINUED</option>
                                                                </select>
                                                              </div>
                                                              <div className="h-6">
                                                                <input 
                                                                  type="number" 
                                                                  defaultValue={item.stock_quantity || ''}
                                                                  placeholder="Stock Qty"
                                                                  className="w-full h-full bg-transparent border-none text-white text-xs text-center p-0"
                                                                  onBlur={async (e) => {
                                                                    const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
                                                                    await fetch(`${backendUrl}/api/items/${item.id}`, {
                                                                      method: 'PUT',
                                                                      headers: { 'Content-Type': 'application/json' },
                                                                      body: JSON.stringify({ stock_quantity: parseInt(e.target.value) || 0 })
                                                                    });
                                                                    console.log('Stock qty saved:', e.target.value);
                                                                  }}
                                                                />
                                                              </div>
                                                            </div>
                                                          </td>
                                                          
                                                          {/* RESTOCK DATE/LEAD TIME - STACKED */}
                                                          <td className="border border-[#D4A574] px-1 py-1 text-sm">
                                                            <div className="flex flex-col h-full">
                                                              <div className="h-6 mb-1">
                                                                <input 
                                                                  type="date"
                                                                  defaultValue={item.restock_date || ''}
                                                                  className="w-full h-full bg-transparent border-none text-white text-xs p-0"
                                                                  onBlur={async (e) => {
                                                                    const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
                                                                    await fetch(`${backendUrl}/api/items/${item.id}`, {
                                                                      method: 'PUT',
                                                                      headers: { 'Content-Type': 'application/json' },
                                                                      body: JSON.stringify({ restock_date: e.target.value })
                                                                    });
                                                                    console.log('Restock date saved:', e.target.value);
                                                                  }}
                                                                />
                                                              </div>
                                                              <div className="h-6">
                                                                <input 
                                                                  type="number"
                                                                  defaultValue={item.lead_time_weeks || ''}
                                                                  placeholder="Lead (wks)"
                                                                  className="w-full h-full bg-transparent border-none text-[#D4A574] text-xs text-center p-0"
                                                                  onBlur={async (e) => {
                                                                    const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;
                                                                    await fetch(`${backendUrl}/api/items/${item.id}`, {
                                                                      method: 'PUT',
                                                                      headers: { 'Content-Type': 'application/json' },
                                                                      body: JSON.stringify({ lead_time_weeks: e.target.value })
                                                                    });
                                                                    console.log('Lead time saved:', e.target.value);
                                                                  }}
                                                                />
                                                              </div>
                                                            </div>
                                                          </td>
                                                          
                                                          {/* RIGHT SIDE - STACKED COLUMNS AS USER SPECIFIED */}
                                                          
                                                          {/* Order Date (ALONE) */}
                                                          <td className="border border-[#B49B7E] px-2 py-2 text-sm text-[#B49B7E]">
                                                            <input 
                                                              type="date" 
                                                              className="w-full bg-transparent border-none text-white text-sm"
                                                              onChange={(e) => console.log('Order date changed:', e.target.value)}
                                                            />
                                                          </td>
                                                          
                                                          {/* Order Status/Order Number (STACKED VERTICALLY) - COLORED BY STATUS */}
                                                          <td className="border border-[#B49B7E] px-1 py-1 text-sm" style={{
                                                            background: item.status ? `linear-gradient(135deg, ${getStatusColor(item.status)}FF 0%, ${getStatusColor(item.status)}AA 20%, ${getStatusColor(item.status)} 40%, ${getStatusColor(item.status)}AA 80%, ${getStatusColor(item.status)}FF 100%)` : 'transparent',
                                                            boxShadow: item.status ? `0 0 15px ${getStatusColor(item.status)}40, inset 0 0 30px rgba(255, 255, 255, 0.1), inset 0 0 50px rgba(0, 0, 0, 0.3)` : 'none'
                                                          }}>
                                                            <div className="flex flex-col h-full">
                                                              <div className="h-6 mb-1">
                                                                <select 
                                                                  className="w-full h-full bg-transparent border-none text-white text-xs p-0"
                                                                  value={item.status || ''}
                                                                  style={{ backgroundColor: getStatusColor(item.status || '') }}
                                                                  onChange={(e) => {
                                                                    const newStatus = e.target.value;
                                                                    handleStatusChange(item.id, newStatus);
                                                                  }}
                                                                >
                                                                  <option value="" style={{ backgroundColor: getStatusColor(''), color: 'white' }}>—</option>
                                                                  <option value="TO BE SELECTED" style={{ backgroundColor: getStatusColor('TO BE SELECTED'), color: 'white' }}>TO BE SELECTED</option>
                                                                  <option value="RESEARCHING" style={{ backgroundColor: getStatusColor('RESEARCHING'), color: 'white' }}>RESEARCHING</option>
                                                                  <option value="PENDING APPROVAL" style={{ backgroundColor: getStatusColor('PENDING APPROVAL'), color: 'white' }}>PENDING APPROVAL</option>
                                                                  <option value="APPROVED" style={{ backgroundColor: getStatusColor('APPROVED'), color: 'white' }}>APPROVED</option>
                                                                  <option value="ORDERED" style={{ backgroundColor: getStatusColor('ORDERED'), color: 'white' }}>ORDERED</option>
                                                                  <option value="REPLACEMENT" style={{ backgroundColor: getStatusColor('REPLACEMENT'), color: 'white' }}>REPLACEMENT</option>
                                                                  <option value="CONFIRMED" style={{ backgroundColor: getStatusColor('CONFIRMED'), color: 'white' }}>CONFIRMED</option>
                                                                  <option value="IN PRODUCTION" style={{ backgroundColor: getStatusColor('IN PRODUCTION'), color: 'white' }}>IN PRODUCTION</option>
                                                                  <option value="SHIPPED" style={{ backgroundColor: getStatusColor('SHIPPED'), color: 'white' }}>SHIPPED</option>
                                                                  <option value="IN TRANSIT" style={{ backgroundColor: getStatusColor('IN TRANSIT'), color: 'white' }}>IN TRANSIT</option>
                                                                  <option value="OUT FOR DELIVERY" style={{ backgroundColor: getStatusColor('OUT FOR DELIVERY'), color: 'white' }}>OUT FOR DELIVERY</option>
                                                                  <option value="DELIVERED TO RECEIVER" style={{ backgroundColor: getStatusColor('DELIVERED TO RECEIVER'), color: 'white' }}>DELIVERED TO RECEIVER</option>
                                                                  <option value="DELIVERED TO JOB SITE" style={{ backgroundColor: getStatusColor('DELIVERED TO JOB SITE'), color: 'white' }}>DELIVERED TO JOB SITE</option>
                                                                  <option value="RECEIVED" style={{ backgroundColor: getStatusColor('RECEIVED'), color: 'white' }}>RECEIVED</option>
                                                                  <option value="READY FOR INSTALL" style={{ backgroundColor: getStatusColor('READY FOR INSTALL'), color: 'white' }}>READY FOR INSTALL</option>
                                                                  <option value="INSTALLING" style={{ backgroundColor: getStatusColor('INSTALLING'), color: 'white' }}>INSTALLING</option>
                                                                  <option value="INSTALLED" style={{ backgroundColor: getStatusColor('INSTALLED'), color: 'white' }}>INSTALLED</option>
                                                                  <option value="ON HOLD" style={{ backgroundColor: getStatusColor('ON HOLD'), color: 'white' }}>ON HOLD</option>
                                                                  <option value="BACKORDERED" style={{ backgroundColor: getStatusColor('BACKORDERED'), color: 'white' }}>BACKORDERED</option>
                                                                  <option value="DAMAGED" style={{ backgroundColor: getStatusColor('DAMAGED'), color: 'white' }}>DAMAGED</option>
                                                                  <option value="RETURNED" style={{ backgroundColor: getStatusColor('RETURNED'), color: 'white' }}>RETURNED</option>
                                                                  <option value="CANCELLED" style={{ backgroundColor: getStatusColor('CANCELLED'), color: 'white' }}>CANCELLED</option>
                                                                </select>
                                                              </div>
                                                              <div className="h-6">
                                                                <input 
                                                                  type="text" 
                                                                  placeholder="Order #"
                                                                  className="w-full h-full bg-transparent border-none text-white text-xs p-0"
                                                                  onChange={(e) => console.log('Order number changed:', e.target.value)}
                                                                />
                                                              </div>
                                                            </div>
                                                          </td>
                                                          
                                                          {/* Estimated Ship Date/Estimated Delivery Date (STACKED VERTICALLY) */}
                                                          <td className="border border-[#B49B7E] px-1 py-1 text-sm">
                                                            <div className="flex flex-col h-full">
                                                              <div className="h-6 mb-1">
                                                                <input 
                                                                  type="date" 
                                                                  className="w-full h-full bg-transparent border-none text-white text-xs p-0"
                                                                  onChange={(e) => console.log('Estimated ship date changed:', e.target.value)}
                                                                />
                                                              </div>
                                                              <div className="h-6">
                                                                <input 
                                                                  type="date" 
                                                                  className="w-full h-full bg-transparent border-none text-white text-xs p-0"
                                                                  onChange={(e) => console.log('Estimated delivery date changed:', e.target.value)}
                                                                />
                                                              </div>
                                                            </div>
                                                          </td>
                                                          
                                                          {/* Install Date/Ship To (STACKED VERTICALLY) */}
                                                          <td className="border border-[#B49B7E] px-1 py-1 text-sm">
                                                            <div className="flex flex-col h-full">
                                                              <div className="h-6 mb-1">
                                                                <input 
                                                                  type="date" 
                                                                  className="w-full h-full bg-transparent border-none text-white text-xs p-0"
                                                                  onChange={(e) => console.log('Install date changed:', e.target.value)}
                                                                />
                                                              </div>
                                                              <div className="h-6">
                                                                <select 
                                                                  className="w-full h-full bg-transparent border-none text-white text-xs p-0"
                                                                  onChange={(e) => console.log('Ship to changed:', e.target.value)}
                                                                >
                                                                  <option value="">Ship To...</option>
                                                                  <option value="CLIENT HOME">CLIENT HOME</option>
                                                                  <option value="JOB SITE">JOB SITE</option>
                                                                  <option value="DESIGN CENTER">DESIGN CENTER</option>
                                                                  <option value="WAREHOUSE">WAREHOUSE</option>
                                                                  <option value="VENDOR LOCATION">VENDOR LOCATION</option>
                                                                  <option value="CLASSIC DESIGN SERVICES">CLASSIC DESIGN SERVICES</option>
                                                                  <option value="RECEIVER">RECEIVER</option>
                                                                  <option value="ADD_NEW">+ Add New Location</option>
                                                                </select>
                                                              </div>
                                                            </div>
                                                          </td>
                                                          
                                                          {/* Tracking/Carrier (STACKED VERTICALLY) - COLORED BY CARRIER */}
                                                          <td className="border border-[#B49B7E] px-1 py-1 text-sm" style={{
                                                            background: item.carrier ? `linear-gradient(135deg, ${getCarrierColor(item.carrier)}FF 0%, ${getCarrierColor(item.carrier)}AA 20%, ${getCarrierColor(item.carrier)} 40%, ${getCarrierColor(item.carrier)}AA 80%, ${getCarrierColor(item.carrier)}FF 100%)` : 'transparent',
                                                            boxShadow: item.carrier ? `0 0 15px ${getCarrierColor(item.carrier)}40, inset 0 0 30px rgba(255, 255, 255, 0.1), inset 0 0 50px rgba(0, 0, 0, 0.3)` : 'none'
                                                          }}>
                                                            <div className="flex flex-col h-full">
                                                              <div className="h-6 mb-1">
                                                                <input 
                                                                  type="text" 
                                                                  placeholder="Live Tracking #"
                                                                  className="w-full h-full bg-transparent border-none text-white text-xs p-0"
                                                                  onChange={(e) => console.log('Live tracking changed:', e.target.value)}
                                                                />
                                                              </div>
                                                              <div className="h-6">
                                                                <select 
                                                                  className="w-full h-full bg-transparent border-none text-white text-xs p-0"
                                                                  value={item.carrier || ''}
                                                                  style={{ backgroundColor: getCarrierColor(item.carrier || '') }}
                                                                  onChange={(e) => handleCarrierChange(item.id, e.target.value)}
                                                                >
                                                                  <option value="" style={{ backgroundColor: '#6B7280', color: 'white' }}>—</option>
                                                                  <option value="FedEx" style={{ backgroundColor: '#FF6600', color: 'white' }}>FedEx</option>
                                                                  <option value="FedEx Ground" style={{ backgroundColor: '#FF6600', color: 'white' }}>FedEx Ground</option>
                                                                  <option value="UPS" style={{ backgroundColor: '#8B4513', color: 'white' }}>UPS</option>
                                                                  <option value="UPS Ground" style={{ backgroundColor: '#8B4513', color: 'white' }}>UPS Ground</option>
                                                                  <option value="USPS" style={{ backgroundColor: '#004B87', color: 'white' }}>USPS</option>
                                                                  <option value="DHL" style={{ backgroundColor: '#FFD700', color: 'black' }}>DHL</option>
                                                                  <option value="Brooks" style={{ backgroundColor: '#4682B4', color: 'white' }}>Brooks</option>
                                                                  <option value="Zenith" style={{ backgroundColor: '#20B2AA', color: 'white' }}>Zenith</option>
                                                                  <option value="Sunbelt" style={{ backgroundColor: '#FF4500', color: 'white' }}>Sunbelt</option>
                                                                  <option value="R+L Carriers" style={{ backgroundColor: '#32CD32', color: 'white' }}>R+L Carriers</option>
                                                                  <option value="Yellow Freight" style={{ backgroundColor: '#FFD700', color: 'black' }}>Yellow Freight</option>
                                                                  <option value="XPO Logistics" style={{ backgroundColor: '#6A5ACD', color: 'white' }}>XPO Logistics</option>
                                                                  <option value="Old Dominion" style={{ backgroundColor: '#DC143C', color: 'white' }}>Old Dominion</option>
                                                                  <option value="ABF Freight" style={{ backgroundColor: '#FF6347', color: 'white' }}>ABF Freight</option>
                                                                  <option value="Con-Way" style={{ backgroundColor: '#48D1CC', color: 'white' }}>Con-Way</option>
                                                                  <option value="Estes Express" style={{ backgroundColor: '#9370DB', color: 'white' }}>Estes Express</option>
                                                                  <option value="YRC Freight" style={{ backgroundColor: '#FF1493', color: 'white' }}>YRC Freight</option>
                                                                  <option value="Saia" style={{ backgroundColor: '#00CED1', color: 'white' }}>Saia</option>
                                                                  <option value="OTHER" style={{ backgroundColor: '#808080', color: 'white' }}>OTHER</option>
                                                                  <option value="ADD_NEW" style={{ backgroundColor: '#6B7280', color: 'white' }}>+ Add New Carrier</option>
                                                                </select>
                                                              </div>
                                                            </div>
                                                          </td>
                                                          
                                                          {/* NOTES */}
                                                          <td className="border border-[#B49B7E] px-2 py-2 text-sm text-[#B49B7E]">
                                                            <input 
                                                              type="text" 
                                                              placeholder="Notes"
                                                              defaultValue={item.notes || ''}
                                                              className="w-full bg-transparent border-none text-white text-sm"
                                                              onBlur={(e) => handleUpdateItemField(item.id, 'notes', e.target.value)}
                                                            />
                                                          </td>
                                                          
                                                          {/* REMARKS - EDITABLE */}
                                                          <td className="border border-[#B49B7E] px-2 py-2 text-sm text-[#B49B7E]">
                                                            <input 
                                                              type="text" 
                                                              placeholder="Remarks"
                                                              defaultValue={item.remarks || ''}
                                                              className="w-full bg-transparent border-none text-white text-sm"
                                                              onBlur={(e) => handleUpdateItemField(item.id, 'remarks', e.target.value)}
                                                            />
                                                          </td>
                                                          
                                                          {/* INSTALL NOTES - EDITABLE */}
                                                          <td className="border border-[#B49B7E] px-2 py-2 text-sm text-[#B49B7E]" style={{ background: 'rgba(5, 150, 105, 0.15)' }}>
                                                            <input 
                                                              type="text" 
                                                              placeholder="Install notes..."
                                                              defaultValue={item.install_notes || ''}
                                                              className="w-full bg-transparent border-none text-green-300 text-sm"
                                                              onBlur={(e) => handleUpdateItemField(item.id, 'install_notes', e.target.value)}
                                                            />
                                                          </td>
                                                          
                                                          {/* LINK WITH CLICKABLE OPEN BUTTON AND SCRAPE */}
                                                          <td className="border border-[#B49B7E] px-1 py-1 text-white w-32">
                                                            <div className="flex flex-col gap-1">
                                                              <input 
                                                                type="text" 
                                                                value={item.link || item.link_url || ''}
                                                                placeholder="Product URL"
                                                                className="w-full bg-transparent text-blue-400 text-xs outline-none border border-gray-600 rounded px-1"
                                                                onChange={(e) => {
                                                                  // Update item link immediately for scraping
                                                                  item.link = e.target.value;
                                                                  item.link_url = e.target.value;
                                                                }}
                                                                onBlur={(e) => {
                                                                  const newValue = e.target.value?.trim();
                                                                  if (newValue !== item.link) {
                                                                    handleUpdateItemField(item.id, 'link', newValue);
                                                                  }
                                                                }}
                                                              />
                                                              <div className="flex gap-1">
                                                                {(item.link || item.link_url) && (
                                                                  <a
                                                                    href={item.link || item.link_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    data-testid={`vendor-link-${item.id}`}
                                                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 rounded text-center font-bold"
                                                                    title="Open the vendor product page in a new tab"
                                                                  >
                                                                    🔗 View at vendor
                                                                  </a>
                                                                )}
                                                                <button
                                                                  onClick={() => handleScrapeProduct(item.link || item.link_url, item.id)}
                                                                  className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 rounded"
                                                                  disabled={!item.link && !item.link_url}
                                                                  title="Scrape product information from URL"
                                                                >
                                                                  SCRAPE
                                                                </button>
                                                              </div>
                                                            </div>
                                                          </td>
                                                          
                                                          {/* ACTIONS - CUT, COPY, PASTE & DELETE */}
                                                          <td className="border border-[#B49B7E] px-2 py-2 text-center">
                                                            <div className="flex items-center justify-center gap-1">
                                                              {/* CUT BUTTON */}
                                                              <button 
                                                                onClick={() => {
                                                                  window.ffeClipboardCut = {
                                                                    id: item.id,
                                                                    name: item.name || '',
                                                                    vendor: item.vendor || '',
                                                                    sku: item.sku || '',
                                                                    cost: item.cost || 0,
                                                                    size: item.size || '',
                                                                    finish_color: item.finish_color || '',
                                                                    finish_image: item.finish_image || '',
                                                                    quantity: item.quantity || null,
                                                                    status: item.status || '',
                                                                    link: item.link || '',
                                                                    image_url: item.image_url || '',
                                                                    remarks: item.remarks || '',
                                                                    notes: item.notes || ''
                                                                  };
                                                                  window.ffeClipboardCopy = null;
                                                                  alert('✂️ Item CUT! Click 📥 on any row to MOVE it there.');
                                                                  if (onReload) onReload();
                                                                }}
                                                                className="text-orange-400 hover:text-orange-300 text-sm"
                                                                title="Cut Item (MOVE)"
                                                              >
                                                                ✂️
                                                              </button>
                                                              {/* COPY BUTTON */}
                                                              <button 
                                                                onClick={() => {
                                                                  window.ffeClipboardCopy = {
                                                                    name: item.name || '',
                                                                    vendor: item.vendor || '',
                                                                    sku: item.sku || '',
                                                                    cost: item.cost || 0,
                                                                    size: item.size || '',
                                                                    finish_color: item.finish_color || '',
                                                                    finish_image: item.finish_image || '',
                                                                    quantity: item.quantity || null,
                                                                    link: item.link || '',
                                                                    image_url: item.image_url || '',
                                                                    remarks: item.remarks || '',
                                                                    notes: item.notes || ''
                                                                  };
                                                                  window.ffeClipboardCut = null;
                                                                  alert('📋 Item COPIED! Click 📥 on any row to paste.');
                                                                }}
                                                                className="text-blue-400 hover:text-blue-300 text-sm"
                                                                title="Copy Item"
                                                              >
                                                                📋
                                                              </button>
                                                              {/* PASTE BUTTON - Always visible */}
                                                              <button 
                                                                onClick={async () => {
                                                                  const hasData = window.ffeClipboardCut || window.ffeClipboardCopy || scraperClipboard;
                                                                  if (!hasData) {
                                                                    alert('Nothing to paste! Use ✂️ CUT or 📋 COPY first, or scrape a product.');
                                                                    return;
                                                                  }
                                                                  
                                                                  // If scraper clipboard, use that
                                                                  if (scraperClipboard && !window.ffeClipboardCut && !window.ffeClipboardCopy) {
                                                                    handlePasteScrapedData(item.id);
                                                                    return;
                                                                  }
                                                                  
                                                                  try {
                                                                    const backendUrl = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
                                                                    const isCut = !!window.ffeClipboardCut;
                                                                    const clipData = isCut ? window.ffeClipboardCut : window.ffeClipboardCopy;
                                                                    
                                                                    if (isCut) {
                                                                      // MOVE: Update existing item's subcategory
                                                                      const response = await fetch(`${backendUrl}/api/items/${clipData.id}`, {
                                                                        method: 'PUT',
                                                                        headers: { 'Content-Type': 'application/json' },
                                                                        body: JSON.stringify({
                                                                          subcategory_id: category.subcategories?.[0]?.id,
                                                                          order_index: (item.order_index || 0) + 1
                                                                        })
                                                                      });
                                                                      if (response.ok) {
                                                                        window.ffeClipboardCut = null;
                                                                        if (onReload) onReload();
                                                                      } else {
                                                                        alert('Failed to move item');
                                                                      }
                                                                    } else {
                                                                      // COPY: Create new item
                                                                      const pasteData = {
                                                                        name: clipData.name ? `${clipData.name} (Copy)` : '',
                                                                        vendor: clipData.vendor || '',
                                                                        sku: clipData.sku || '',
                                                                        cost: clipData.cost || 0,
                                                                        size: clipData.size || '',
                                                                        finish_color: clipData.finish_color || '',
                                                                        finish_image: clipData.finish_image || '',
                                                                        quantity: clipData.quantity || null,
                                                                        status: '',
                                                                        link: clipData.link || '',
                                                                        image_url: clipData.image_url || '',
                                                                        remarks: clipData.remarks || '',
                                                                        notes: clipData.notes || '',
                                                                        subcategory_id: category.subcategories?.[0]?.id,
                                                                        order_index: (item.order_index || 0) + 1
                                                                      };
                                                                      const response = await fetch(`${backendUrl}/api/items`, {
                                                                        method: 'POST',
                                                                        headers: { 'Content-Type': 'application/json' },
                                                                        body: JSON.stringify(pasteData)
                                                                      });
                                                                      if (response.ok) {
                                                                        window.ffeClipboardCopy = null;
                                                                        if (onReload) onReload();
                                                                      } else {
                                                                        alert('Failed to paste item');
                                                                      }
                                                                    }
                                                                  } catch (error) {
                                                                    alert('Error: ' + error.message);
                                                                  }
                                                                }}
                                                                className={`text-sm ${(window.ffeClipboardCut || window.ffeClipboardCopy || scraperClipboard) ? 'text-green-400 hover:text-green-300 animate-pulse' : 'text-gray-500'}`}
                                                                title={window.ffeClipboardCut ? "MOVE cut item here" : window.ffeClipboardCopy ? "Paste copied item here" : scraperClipboard ? `Paste: ${scraperClipboard.name}` : "Cut/Copy an item first"}
                                                              >
                                                                📥
                                                              </button>
                                                              <button 
                                                                onClick={() => handleDeleteItem(item.id)}
                                                                className="bg-red-600 hover:bg-red-500 text-white text-xs px-2 py-1.5 rounded"
                                                                title="Delete Item"
                                                              >
                                                                🗑️
                                                              </button>
                                                            </div>
                                                          </td>
                                                        </tr>
                                                        </React.Fragment>
                                                        );
                                                        });
                                                      })()}
                                                      {itemsDropProvided.placeholder}
                                                      </>
                                                      )}
                                                      </Droppable>
                                                      
                                                      {/* INLINE ADD ITEM ROW */}
                                                      <tr className="hover:bg-[#1a1a2a]">
                                                        <td colSpan="14" className="border border-[#B49B7E]/30 px-2 py-1">
                                                          <button
                                                            onClick={() => {
                                                              const firstSubcategory = category.subcategories?.[0];
                                                              if (firstSubcategory) {
                                                                setSelectedSubCategoryId(firstSubcategory.id);
                                                              }
                                                              setShowAddItem(true);
                                                            }}
                                                            className="text-[#D4A574] hover:text-[#E8D4B8] text-xs flex items-center gap-1 opacity-60 hover:opacity-100 transition-opacity"
                                                          >
                                                            <span>➕</span>
                                                            <span>Add item to {category.name}</span>
                                                          </button>
                                                        </td>
                                                      </tr>
                                                      
                                                      {/* BUTTONS ROW - LEFT ALIGNED WITH GOLD COLOR */}
                                                      <tr>
                                                        <td colSpan="7" className="border border-[#B49B7E] px-6 py-2 bg-slate-900">
                                                          <div className="flex justify-start items-center space-x-4">
                                                            {/* Add Item Button - GOLD/AMBER COLOR */}
                                                            <button
                                                              onClick={() => setShowAddItem(true)}
                                                              className="bg-amber-700 hover:bg-amber-600 text-white px-3 py-1 rounded text-sm font-medium"
                                                            >
                                                              ✚ Add Item
                                                            </button>
                                                            
                                                            {/* Add Category Dropdown - GOLD/AMBER COLOR */}
                                                            <select
                                                              value=""
                                                              onChange={(e) => {
                                                                if (e.target.value === 'CREATE_NEW') {
                                                                  const categoryName = prompt('Enter new category name:');
                                                                  if (categoryName && categoryName.trim()) {
                                                                    handleAddCategory(room.id, categoryName.trim());
                                                                  }
                                                                } else if (e.target.value) {
                                                                  handleAddCategory(room.id, e.target.value);
                                                                }
                                                              }}
                                                              className="bg-amber-700 hover:bg-amber-600 text-white px-3 py-1 rounded text-sm font-medium border-none outline-none"
                                                            >
                                                              <option value="">Add Category ▼</option>
                                                              {/* Driven by the server's own category list rather than a
                                                                  hardcoded one. The hardcoded version offered eight names
                                                                  the server had never heard of ("Paint, Wallpaper, and
                                                                  Finishes", "Plumbing & Fixtures", ...), so picking one
                                                                  produced a bare category with none of its sections.
                                                                  Reading the list from the source means the two can no
                                                                  longer drift apart. */}
                                                              {availableCategories.map(category => (
                                                                <option key={category} value={category}>{category}</option>
                                                              ))}
                                                              <option value="CREATE_NEW">+ Create New Category</option>
                                                            </select>

                                                            {/* Add Sub-Category Dropdown - pick a category, then name the sub-category */}
                                                            <select
                                                              value=""
                                                              onChange={(e) => {
                                                                const val = e.target.value;
                                                                e.target.value = '';
                                                                if (!val) return;
                                                                if (val === 'CUSTOM_SUB') {
                                                                  const subName = prompt('Enter custom sub-category name:');
                                                                  if (!subName || !subName.trim()) return;
                                                                  const cats = room.categories || [];
                                                                  if (cats.length === 0) { alert('Add a category first, then add a sub-category to it.'); return; }
                                                                  let target = cats[0];
                                                                  if (cats.length > 1) {
                                                                    const choice = prompt('Add "' + subName.trim() + '" to which category?\n\n' + cats.map((c, i) => (i + 1) + '. ' + c.name).join('\n') + '\n\nEnter the number:');
                                                                    const idx = parseInt(choice, 10) - 1;
                                                                    if (isNaN(idx) || idx < 0 || idx >= cats.length) { alert('Cancelled - invalid category number.'); return; }
                                                                    target = cats[idx];
                                                                  }
                                                                  handleAddSubcategory(target.id, subName.trim());
                                                                  return;
                                                                }
                                                                const subName = prompt('Enter new sub-category name:');
                                                                if (subName && subName.trim()) {
                                                                  handleAddSubcategory(val, subName.trim());
                                                                }
                                                              }}
                                                              className="bg-amber-700 hover:bg-amber-600 text-white px-3 py-1 rounded text-sm font-medium border-none outline-none"
                                                            >
                                                              <option value="">Add Sub-Category ▼</option>
                                                              {room.categories?.map(cat => (
                                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                              ))}
                                                              <option value="CUSTOM_SUB">➕ Create Custom Sub-Category</option>
                                                            </select>

                                                            {/* Delete Section Button - RED COLOR */}
                                                            <button
                                                              onClick={() => handleDeleteRoom(room.id)}
                                                              className="bg-red-700 hover:bg-red-600 text-white px-3 py-1 rounded text-sm font-medium"
                                                            >
                                                              🗑️ Delete Section
                                                            </button>
                                                          </div>
                                                        </td>
                                                      </tr>
                                                    </React.Fragment>
                                                  )}
                                                </>
                                              )}
                                            </Draggable>
                                          );
                                        })}
                                        {provided.placeholder}
                                      </>
                                    )}
                                  </Droppable>
                                )}
                        </>
                      )}
                    </Draggable>
                    </React.Fragment>
                  );
                  });
                })()}
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                </DragDropContext>
                </table>
            </div>
          </div>
        </div>

      {/* ADD INSTALLED MODAL */}

      {showAddItem && (
        <AddItemModal
          onClose={() => setShowAddItem(false)}
          onSubmit={handleAddItem}
          itemStatuses={itemStatuses}
          vendorTypes={vendorTypes}
          loading={false}
          projectId={project?.id}
        />
      )}

      {/* PHOTOS ON A LINE ITEM */}
      {photoItem && (
        <ItemPhotosModal
          item={photoItem}
          onClose={() => setPhotoItem(null)}
          onSaved={(photos) => {
            // Keep the row's counter in step without a full reload.
            photoItem.photos = photos;
            setPhotoItem({ ...photoItem, photos });
            if (onReload) onReload();
          }}
        />
      )}

      {/* MOVE ITEM MODAL — reliable "grab & move" to another room / category */}
      {moveItem && createPortal((
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100002] p-4"
          data-testid="move-item-modal"
          onClick={() => !moveBusy && setMoveItem(null)}
        >
          <div
            className="bg-[#1a1a1a] border border-[#8b7355] rounded-xl w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-[#8b7355]/40">
              <div className="text-[#D4A574] font-bold text-base flex items-center gap-2">
                <FolderInput size={18} /> {moveItem.bulk ? `Move ${selectedItemIds.size} items` : 'Move item'}
              </div>
              <div className="text-stone-400 text-xs mt-1 truncate" title={moveItem.bulk ? '' : moveItem.name}>
                {moveItem.bulk ? 'Pick where all selected items should go' : (moveItem.name || 'Item')}
              </div>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-stone-400 mb-1">Room</label>
                <select
                  data-testid="move-room-select"
                  value={moveRoomId}
                  onChange={(e) => onMoveRoomChange(e.target.value)}
                  className="w-full bg-black/40 border border-[#8b7355]/60 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4A574]"
                >
                  {(project?.rooms || []).map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-stone-400 mb-1">Category</label>
                <select
                  data-testid="move-category-select"
                  value={moveCategoryId}
                  onChange={(e) => onMoveCategoryChange(e.target.value)}
                  className="w-full bg-black/40 border border-[#8b7355]/60 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4A574]"
                >
                  {(((project?.rooms || []).find((r) => r.id === moveRoomId)?.categories) || []).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-stone-400 mb-1">Section</label>
                <select
                  data-testid="move-subcategory-select"
                  value={moveSubId}
                  onChange={(e) => setMoveSubId(e.target.value)}
                  className="w-full bg-black/40 border border-[#8b7355]/60 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4A574]"
                >
                  {((((project?.rooms || []).find((r) => r.id === moveRoomId)?.categories) || []).find((c) => c.id === moveCategoryId)?.subcategories || []).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[#8b7355]/40 flex justify-end gap-3">
              <button
                data-testid="move-cancel-btn"
                onClick={() => setMoveItem(null)}
                disabled={moveBusy}
                className="px-4 py-2 rounded text-stone-400 hover:text-white disabled:opacity-50"
              >Cancel</button>
              <button
                data-testid="move-confirm-btn"
                onClick={performItemMove}
                disabled={moveBusy || !moveSubId}
                className="px-5 py-2 rounded bg-[#D4A574] text-black font-semibold hover:bg-[#E8D4B8] disabled:opacity-50"
              >{moveBusy ? 'Moving…' : 'Move here'}</button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* IMAGE EXPANSION MODAL (gallery with ← / → navigation) — portaled to
          body so position:fixed is always relative to the viewport (a
          transformed ancestor would otherwise push it far down the page) */}
      {expandedImage && createPortal((
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[100001] p-4 overflow-y-auto"
          onClick={() => setExpandedImage(null)}
          data-testid="ffe-image-lightbox"
        >
          <div className="relative max-w-4xl max-h-screen" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute -top-3 -right-3 bg-red-600 hover:bg-red-700 text-white rounded-full w-10 h-10 flex items-center justify-center text-2xl font-bold z-20 shadow-lg"
              title="Close (Esc)"
              data-testid="ffe-lightbox-close"
            >
              ×
            </button>
            {expandedImage.images && expandedImage.images.length > 1 && (
              <>
                <button
                  onClick={() => stepLightbox(-1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/90 text-white rounded-full w-12 h-12 flex items-center justify-center text-3xl z-20 shadow-lg"
                  title="Previous (←)"
                  data-testid="ffe-lightbox-prev"
                >
                  ‹
                </button>
                <button
                  onClick={() => stepLightbox(1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/90 text-white rounded-full w-12 h-12 flex items-center justify-center text-3xl z-20 shadow-lg"
                  title="Next (→)"
                  data-testid="ffe-lightbox-next"
                >
                  ›
                </button>
                <div
                  className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1 rounded-full z-20"
                  data-testid="ffe-lightbox-counter"
                >
                  {expandedImage.index + 1} / {expandedImage.images.length}
                </div>
              </>
            )}
            <img 
              src={expandedImage.images ? expandedImage.images[expandedImage.index] : expandedImage} 
              alt="Expanded view" 
              className="max-w-full max-h-[88vh] object-contain rounded-lg"
            />
          </div>
        </div>
      ), document.body)}

      {/* PHOTO VIEWER MODAL — also portaled to body for correct viewport centering */}
      {selectedPhotoView && createPortal((
        <div 
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-[100001] p-4"
          onClick={() => setSelectedPhotoView(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh]">
            <button
              onClick={() => setSelectedPhotoView(null)}
              className="absolute -top-4 -right-4 bg-red-600 hover:bg-red-700 text-white rounded-full w-10 h-10 flex items-center justify-center text-2xl font-bold z-10"
            >
              ×
            </button>
            <img 
              src={selectedPhotoView.photo_data || selectedPhotoView.url || selectedPhotoView.image_url} 
              alt={selectedPhotoView.file_name || 'Photo'} 
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            {selectedPhotoView.notes && (
              <div className="mt-2 text-center text-[#D4A574] bg-black/80 px-4 py-2 rounded">
                {selectedPhotoView.notes}
              </div>
            )}
          </div>
        </div>
      ), document.body)}
    </div>
  );
};

export default ExactFFESpreadsheet;